import React, { useState, useEffect, useRef } from 'react';
import './Project.css';
import logo from './assets/dg-logo.png';
import userIcon from './assets/user-icon.png';
import Navigation from './Navigation';
import { useParams } from 'react-router-dom';
import ManningModal from "./ManningModal";
import { fetchWithAuth, fetchWithAuthV2} from './utils';
import AbsenceModal, { AbsenceModalHandle } from "./AbsenceModal";
import type { AbsenceSavePayload } from "./AbsenceModal";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import pdfIcon from "./assets/pdf-download.png";


interface Discipline {
  discipline_code: string;
  discipline_name: string;
}

interface Manning {
  manning_info_id: string;
  name: string;
  leader_name?: string; // ✅ add
  region_code?: string;
  office_code?: string;
  email?: string;
  title?: string;
  type?: string;
  primary_practice?: string;
  discipline?: string;
  experience?: string;
  current_market_sector?: string;
  company_code?: string;
}

interface Region {
  company_code: string;
  region_code: string;
  region_name: string;
}

interface Project {
  project_code: string;
  project_name: string;
  client_name: string;
  project_description: string;
  project_status: string;
  project_hours?: number;
  multi_region_flag?: boolean;
}


// ===== Discipline Tasks =====
interface ProjectDisciplineTask {
  project_discipline_task_id: number;
  task_description: string;
  total_forecasted_hours: number;
  remaining_hours: number;
}

interface HoursRow {
  manning_info_id: string;
  hours: number[];
}

type ToastType = 'success' | 'error' | 'info';

interface ToastState {
  message: string;
  type: ToastType;
}

interface RawManningUnavailability {
  weekdate: string; // YYYY-MM-DD (Monday)
  days: string;     // "MM-DD,MM-DD,..."
}

interface ManningUnavailability {
  weekdate: string;
  hours: number;
}


/** Discipline-level forecast record (from get_all_project_discipline_forecast) */
interface ProjectDisciplineForecast {
  project_code: string;
  discipline: string;
  project_start_date: string; // ISO date string
  project_end_date: string;   // ISO date string
  project_manager_manning_id: number | string;
  [key: string]: any;
}

/** Manning hours row (from get_project_discipline_forecast_manning_hours) */
interface DisciplineManningHourRow {
  manning_info_id: string;
  weekdate: string; // ISO date (YYYY-MM-DD)
  hours: number;
  project_discipline_task_id?: number;
  discipline: string;
  project_code: string;
  [key: string]: any;
}

interface TotalHoursByWeekRecord {
  weekdate: string;
  total_hours: number;
}

interface ManningProjectForecast {
  project_code: string;
  project_name: string;
  project_hours: number;
  opportunity_flag: number;
  disciplines: {
    [disciplineCode: string]: {
      discipline: string;
      total_discipline_hours: number;
      weekly_hours: {
        weekdate: string;
        hours: number;
      }[];
    };
  };
}

interface ByProjectForecast {
  project_code: string;
  project_name: string;
  project_hours: number;
  opportunity_flag: number;
  disciplines: {
    discipline: string;
    total_discipline_hours: number;
    mannings: {
      manning_info_id: number;
      manning_name: string;
      weekly_hours: {
        weekdate: string;
        hours: number;
      }[];
    }[];
  }[];
}


interface ProjectGroup {
  project_group_id: number;
  project_group_description: string;
}

interface ProjectGroupProject {
  project_group_project_id: number;
  project_code: string;
  project_description?: string;
}


interface ForecastManning {
  manning_info_id: number;
  manning_name: string;
}
interface ForecastProject {
  project_code: string;
  project_name: string;
  discipline: string;
  project_start_date: string;
  project_end_date: string;
  project_manager_manning_id: number;
  status: string;
  total_discipline_hours: number;
}


const Project: React.FC = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const [disciplines, setDisciplines] = useState<Discipline[]>([]);
  const [tempDiscipline, setTempDiscipline] = useState<string>('');
  const [selectedDiscipline, setSelectedDiscipline] = useState<string>('');

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [projectManager, setProjectManager] = useState<string>('');

  // Monday dates (ISO YYYY-MM-DD) for the current discipline forecast sheet
  const [mondayDates, setMondayDates] = useState<string[]>([]);
  // Always effectively a single Manning row
  const [hoursRows, setHoursRows] = useState<HoursRow[]>([]);

  const [manningList, setManningList] = useState<Manning[]>([]);
  const [activeTab, setActiveTab] = useState<number>(2);

  const [isGeneratingForecast, setIsGeneratingForecast] = useState<boolean>(false);

  const [toast, setToast] = useState<ToastState | null>(null);

  const { regionCode } = useParams<{ regionCode: string }>();
  const [searchType, setSearchType] = useState<'code' | 'name'>('code');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<Project[]>([]);

  // Bottom-section forecast data for the currently selected project
  const [projectForecasts, setProjectForecasts] = useState<ProjectDisciplineForecast[]>([]);
  const [projectForecastManningHours, setProjectForecastManningHours] = useState<Record<string, DisciplineManningHourRow[]>>({});
  const [projectNameCache, setProjectNameCache] = useState<Record<string, Project>>({});
  const [totalHoursByManning, setTotalHoursByManning] = useState<Record<string, Record<string, number>>>({});

  const [originalRowSnapshot, setOriginalRowSnapshot] = useState<Record<string, number>>({});

  // Tracks which manning row is being edited at the bottom section
  const [editingRow, setEditingRow] = useState<{
    discipline: string;
    manningId: string;
  } | null>(null);


const absenceModalRef = useRef<AbsenceModalHandle>(null);

const [modalTasks, setModalTasks] = useState<any[]>([]);
const [showAvailabilityCalendar, setShowAvailabilityCalendar] = useState(false);
const [byManningProjects, setByManningProjects] = useState<ManningProjectForecast[]>([]);
const [isFetchingByManning, setIsFetchingByManning] = useState<boolean>(false);

const [disciplineTasks, setDisciplineTasks] = useState<Record<string, ProjectDisciplineTask[]>>({});

// ===== Add / Edit Task Modal =====
const [showTaskModal, setShowTaskModal] = useState(false);
const [taskModalDiscipline, setTaskModalDiscipline] = useState<string | null>(null);
const [taskDescription, setTaskDescription] = useState('');
const [taskHours, setTaskHours] = useState<number>(0);
const [openTaskDiscipline, setOpenTaskDiscipline] = useState<string | null>(null);
const [projectHours, setProjectHours] = useState<number>(0);
const [projectHoursSaved, setProjectHoursSaved] = useState<boolean>(false);
const [disciplineTotalHours, setDisciplineTotalHours] = useState<number | ''>('');
const [isManningModalOpen, setIsManningModalOpen] = useState(false);
const [editingManning, setEditingManning] = useState<any | null>(null);
const [isSavingAbsence, setIsSavingAbsence] = useState(false);
const [absenceSaveSuccess, setAbsenceSaveSuccess] = useState(false);
const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
const [editTaskDescription, setEditTaskDescription] = useState("");
const [editTaskHours, setEditTaskHours] = useState<number>(0);
const [isSavingTask, setIsSavingTask] = useState(false);
const [manningUnavailability, setManningUnavailability] = useState<ManningUnavailability[]>([]);
const [byProjectForecast, setByProjectForecast] = useState<ByProjectForecast | null>(null);
const projectGroupPdfRef = useRef<HTMLDivElement | null>(null);
const byManningPdfRef = useRef<HTMLDivElement | null>(null);
const byProjectPdfRef = useRef<HTMLDivElement | null>(null);
const [isSearchingProject, setIsSearchingProject] = useState(false);
const [showHelpVideo, setShowHelpVideo] = useState(false);
const [videoUrl, setVideoUrl] = useState<string | null>(null);


  const FIXED_DISCIPLINES = [
    { discipline_code: 'CONSTRUCTION', discipline_name: 'Construction' },
    { discipline_code: 'ELECTRICAL', discipline_name: 'Electrical' },
    { discipline_code: 'MECHANICAL', discipline_name: 'Mechanical' },
    { discipline_code: 'CSI', discipline_name: 'CSI' },
  ];

  /* ========== TOAST HELPERS ========== */
  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  };

const [editingDiscipline, setEditingDiscipline] = useState<string | null>(null);

const [editDisciplineState, setEditDisciplineState] = useState({
  start: "",
  end: "",
  pm: "",
  totalHours: "" as number | ""
});

const TASKS_CACHE_KEY = (projectCode: string) => `project_tasks_${projectCode}`;

const cacheProjectTasks = (projectCode: string, tasks: any[]) => {
  const map: Record<string, any> = {};
  tasks.forEach((t) => {
    map[t.project_discipline_task_id] = t;
  });
  localStorage.setItem(TASKS_CACHE_KEY(projectCode), JSON.stringify(map));
};

const handleManningClick = (manningId: string) => {
  const manning = manningList.find(
    (m) => String(m.manning_info_id) === String(manningId)
  );

  if (!manning) return;

  setEditingManning(manning);
  setIsManningModalOpen(true);
};


const getCachedProjectTasks = (projectCode: string): Record<string, any> => {
  const raw = localStorage.getItem(TASKS_CACHE_KEY(projectCode));
  return raw ? JSON.parse(raw) : {};
};


const [showAddManningForDiscipline, setShowAddManningForDiscipline] = useState<Record<string, boolean>>({});

const [mondayDatesByDiscipline, setMondayDatesByDiscipline] =  useState<Record<string, string[]>>({});

// ===== View Forecasts Sub Tabs =====
const [viewForecastTab, setViewForecastTab] = useState<'byManning' | 'byProject' | 'byProjectGroup'>('byManning');

// ===== By Manning =====
const [selectedManningId, setSelectedManningId] = useState<string>('');
// ===== By Manning (Grouped by Project) =====
const [manningForecastByProject, setManningForecastByProject] = useState<Record<string, DisciplineManningHourRow[]>>({});


// ===== By Project =====
const [projectViewSearchType, setProjectViewSearchType] = useState<'byProjectCode' | 'byProjectName' | 'byProjectPM'>('byProjectCode');
const [projectViewHours, setProjectViewHours] = useState<Record<string, ProjectManningWeeks[]>>({});
const [byManningData, setByManningData] = useState<ManningByDiscipline>({});

useEffect(() => {
  if (!selectedProject) return;

  // Load tasks for any discipline that becomes visible
  projectForecasts.forEach((f) => {
    const disciplineCode = f.discipline;
    if (!disciplineTasks[disciplineCode]) {
      fetchDisciplineTasks(disciplineCode);
    }
  });
}, [selectedProject, projectForecasts]);


const [selectedTaskByDiscipline, setSelectedTaskByDiscipline] = useState<Record<string, string>>({});

const [originalHoursByRow, setOriginalHoursByRow] = useState<Record<string, Record<string, number>>>({});

const [showAbsenceModal, setShowAbsenceModal] = useState(false);
const [absenceContext, setAbsenceContext] = useState<{discipline: string;manningId: string;start: string;end: string;} | null>(null);

// ===== By Project (PM Mode) =====
const [pmList, setPmList] = useState<Manning[]>([]);
const [selectedPMId, setSelectedPMId] = useState<string>("");
const [pmProjects, setPmProjects] = useState<ByProjectForecast | null>(null);
const [projectUnavailability, setProjectUnavailability] = useState<Record<string, ManningUnavailability[]>>({});
const [isFetchingByProjectPM, setIsFetchingByProjectPM] = useState(false);

const [projectGroups, setProjectGroups] = useState<ProjectGroup[]>([]);
const [selectedProjectGroupId, setSelectedProjectGroupId] = useState<string>("");
const [projectGroupProjects, setProjectGroupProjects] = useState<ProjectGroupProject[]>([]);

// Add Group Modal
const [showProjectGroupModal, setShowProjectGroupModal] = useState(false);
const [newProjectGroupDescription, setNewProjectGroupDescription] = useState("");
const [newProjectGroupProjectCode, setNewProjectGroupProjectCode] = useState("");
const [isSavingProjectGroup, setIsSavingProjectGroup] = useState(false);

// ===== By Project Group (Forecast Payload) =====
const [projectGroupForecast, setProjectGroupForecast] = useState<any[] | null>(null);
const [isFetchingByProjectGroup, setIsFetchingByProjectGroup] = useState(false);

// ===== Project Group Edit Modal =====
const [editingProjectGroup, setEditingProjectGroup] = useState<ProjectGroup | null>(null);
const [showEditProjectGroupModal, setShowEditProjectGroupModal] = useState(false);

// Editable fields
const [editProjectGroupDescription, setEditProjectGroupDescription] = useState("");
const [editProjectGroupProjects, setEditProjectGroupProjects] = useState<ProjectGroupProject[]>([]);
const [newProjectCodeToAdd, setNewProjectCodeToAdd] = useState("");
const [isUpdatingProjectGroup, setIsUpdatingProjectGroup] = useState(false);
const isExportDisabled =
  (viewForecastTab === "byProjectGroup" && !projectGroupForecast?.length) ||
  (viewForecastTab === "byManning" && !byManningProjects?.length) ||
  (viewForecastTab === "byProject" && !byProjectForecast);


const [forecastManningList, setForecastManningList] = useState<ForecastManning[]>([]);
const [isLoadingForecastManning, setIsLoadingForecastManning] = useState(false);
const [projectViewResults, setProjectViewResults] = useState<ForecastProject[]>([]);
const [isLoadingByProject, setIsLoadingByProject] = useState(false);

useEffect(() => {
  if (viewForecastTab !== "byManning") return;

  const fetchForecastManning = async () => {
    try {
      setIsLoadingForecastManning(true);

      const url =
        "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
        "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
        "&action=get_all_forecast_by_manning";

      const resp = await fetchWithAuth(url);

      setForecastManningList(
        Array.isArray(resp?.records) ? resp.records : []
      );
    } catch (err) {
      console.error("Error fetching forecast manning list", err);
      showToast("Failed to load manning list.", "error");
      setForecastManningList([]);
    } finally {
      setIsLoadingForecastManning(false);
    }
  };

  fetchForecastManning();
}, [viewForecastTab]);

  /* ========== FETCH REGIONS ========== */
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const cached = localStorage.getItem('regions_new');
        if (cached) {
          setRegions(JSON.parse(cached));
        } else {
          const url =
            'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_regions&company_code=DG';
          const data = await fetchWithAuth(url);
          setRegions(data);
          localStorage.setItem('regions_new', JSON.stringify(data));
        }
      } catch (error) {
        console.error('Error fetching regions:', error);
        showToast('Error fetching regions.', 'error');
      }
    };
    fetchRegions();
  }, []);

  useEffect(() => {
    if (!showAbsenceModal || !absenceContext) return;

    // modal is NOW mounted → ref is available
    absenceModalRef.current?.loadUnavailability(absenceContext.manningId);
  }, [showAbsenceModal, absenceContext]);

  useEffect(() => {
  if (viewForecastTab !== "byProject") return;

  const fetchProjectsForByProject = async () => {
    try {
      setIsLoadingByProject(true);

      const url =
        "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
        "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
        "&action=get_all_forecast_by_project";

      const resp = await fetchWithAuth(url);

      setProjectViewResults(
        Array.isArray(resp?.records) ? resp.records : []
      );
    } catch (err) {
      console.error("Error fetching By-Project list", err);
      showToast("Failed to load project list.", "error");
      setProjectViewResults([]);
    } finally {
      setIsLoadingByProject(false);
    }
  };

  // reset dependent state
  setByProjectForecast(null);
  setPmProjects(null);
  setProjectUnavailability({});
  setSelectedPMId("");
  setSearchValue("");

  fetchProjectsForByProject();
}, [viewForecastTab]);


  /* ========== FETCH PROJECTS FOR REGION ========== */
  useEffect(() => {
    const fetchProjects = async () => {
      if (!regionCode) return;

      try {
        const cached = localStorage.getItem(`projects_${regionCode}`);
        if (cached) {
          setProjects(JSON.parse(cached));
        } else {
          const url =
            'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_projects&company_code=DG&region_code=' +
            regionCode;
          const data = await fetchWithAuth(url);
          setProjects(data);
          localStorage.setItem(`projects_${regionCode}`, JSON.stringify(data));
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        showToast('Error fetching projects.', 'error');
      }
    };
    fetchProjects();
  }, [regionCode]);

  /* ========== FETCH DISCIPLINES & MANNING LIST ========== */
  useEffect(() => {
    setDisciplines(FIXED_DISCIPLINES);

    const cachedManning = localStorage.getItem('all_manning');
    if (cachedManning) {
      setManningList(JSON.parse(cachedManning));
    } else {
      const fetchManning = async () => {
        try {
          const url =
            'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_all_manning_pros';
          const data = await fetchWithAuth(url);
          setManningList(data);
          localStorage.setItem('all_manning', JSON.stringify(data));
        } catch (error) {
          console.error('Error fetching manning:', error);
          showToast('Error fetching manning list.', 'error');
        }
      };
      fetchManning();
    }
  }, []);

  /* ========== DATE HELPERS ========== */

  const formatDate = (isoDate?: string | null) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

const formatWeekLabel = (isoDate: string) => {
  const d = new Date(`${isoDate}T00:00:00Z`); // 👈 force UTC
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC', // 👈 critical
  });
};


  const getDisciplineName = (code: string) => {
    const d =
      disciplines.find((disc) => disc.discipline_code === code) ||
      FIXED_DISCIPLINES.find((disc) => disc.discipline_code === code);
    return d ? d.discipline_name : code;
  };

  const getManningNameById = (id: string) => {
    const m = manningList.find((x) => String(x.manning_info_id) === String(id));
    return m ? m.name : id;
  };

  const getProjectManagerName = (id: number | string) => {
    return getManningNameById(String(id));
  };

  /* ========== GENERATE MONDAYS (ALWAYS ONE ROW) ========== */
  const generateMondays = (start: string, end: string) => {
    const result: string[] = [];
    const startObj = new Date(start);
    const endObj = new Date(end);

    if (isNaN(startObj.getTime()) || isNaN(endObj.getTime())) {
      return;
    }

    // Snap start date to Monday
    const day = startObj.getDay(); // 0..6
    const diffToMonday = (day === 0 ? -6 : 1) - day;
    startObj.setDate(startObj.getDate() + diffToMonday);

    while (startObj <= endObj) {
      const iso = startObj.toISOString().split('T')[0]; // YYYY-MM-DD
      result.push(iso);
      startObj.setDate(startObj.getDate() + 7);
    }

    setMondayDates(result);

    const defaultHours = Array(result.length).fill(0);
    setHoursRows([
      {
        manning_info_id: '',
        hours: defaultHours,
      },
    ]);
  };

const fetchTotalHoursByWeek = async (manningId: string) => {
  const url =
    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
    "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
    "&action=get_total_manning_hours_by_week" +
    `&manning_id=${encodeURIComponent(manningId)}`;

  const resp = await fetchWithAuth(url);

  const map: Record<string, number> = {};
  const records: TotalHoursByWeekRecord[] =
    Array.isArray(resp?.records) ? resp.records : [];

  records.forEach((r) => {
    map[r.weekdate.slice(0, 10)] = r.total_hours;
  });

  setTotalHoursByManning((prev) => ({
    ...prev,
    [manningId]: map,
  }));

  return map;
};


  /* ========== TOP SHEET UPDATE HELPERS ========== */
  const updateRowManning = (rowIndex: number, manningId: string) => {
    setHoursRows((prev) => {
      const rows = [...prev];
      rows[rowIndex] = { ...rows[rowIndex], manning_info_id: manningId };
      return rows;
    });
  };

const updateHourInput = (rowIndex: number, colIndex: number, value: number) => {
  setHoursRows((prev) => {
    const rows = [...prev];
    const row = rows[rowIndex] ?? { manning_info_id: "", hours: [] };
    const hours = Array.isArray(row.hours) ? [...row.hours] : [];

    while (hours.length <= colIndex) hours.push(0);
    hours[colIndex] = value;

    rows[rowIndex] = { ...row, hours };
    return rows;
  });
};


  /* ========== FETCH FORECAST DETAILS (BOTTOM SECTION) ========== */

const fetchProjectForecastDetails = async (projectCode: string) => {
  if (!projectCode) return;

  const baseUrl =
    "https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==";

  try {
    // 1) Discipline-level forecast records
    const disciplineUrl =
      baseUrl +
      `&action=get_all_project_discipline_forecast&project_code=${encodeURIComponent(
        projectCode
      )}`;

    const disciplineResp = await fetchWithAuth(disciplineUrl);

    const disciplineRecords: ProjectDisciplineForecast[] = Array.isArray(
      disciplineResp?.records
    )
      ? disciplineResp.records
      : [];

    setProjectForecasts(disciplineRecords);

    // 2) Manning hours per discipline
    const manningMap: Record<string, DisciplineManningHourRow[]> = {};

    await Promise.all(
      disciplineRecords.map(async (f) => {
        const disciplineCode = f.discipline;
        if (!disciplineCode) return;

        const manningUrl =
          baseUrl +
          `&action=get_project_discipline_forecast_manning_hours&project_code=${encodeURIComponent(
            projectCode
          )}&discipline_code=${encodeURIComponent(disciplineCode)}`;

        try {
          const manningResp = await fetchWithAuth(manningUrl);
          const manningRecords: DisciplineManningHourRow[] = Array.isArray(
            manningResp?.records
          )
            ? manningResp.records
            : [];
          manningMap[disciplineCode] = manningRecords;
        } catch (err) {
          console.error(
            "Error fetching manning hours for discipline",
            disciplineCode,
            err
          );
          manningMap[disciplineCode] = [];
        }
      })
    );

    setProjectForecastManningHours(manningMap);

    // 3) Build mondayDatesByDiscipline + showAddManningForDiscipline
    const computedMondays: Record<string, string[]> = {};
    const addManningState: Record<string, boolean> = {};

      disciplineRecords.forEach((f) => {
        const disciplineCode = f.discipline;

        // ✅ FORECAST RANGE IS SOURCE OF TRUTH
        const startIso = f.project_start_date.slice(0, 10);
        const endIso = f.project_end_date.slice(0, 10);

        const weeks = computeMondaysBetweenUTC(startIso, endIso);

        computedMondays[disciplineCode] = weeks;
        addManningState[disciplineCode] = true;
      });


    setMondayDatesByDiscipline(computedMondays);
    setShowAddManningForDiscipline(addManningState);
  } catch (err) {
    console.error("Error fetching project forecast details:", err);
    showToast("Error fetching project forecast details.", "error");

    setProjectForecasts([]);
    setProjectForecastManningHours({});
    setMondayDatesByDiscipline({});
    setShowAddManningForDiscipline({});
  }
};

useEffect(() => {
  if (
    viewForecastTab === "byProject" &&
    projectViewResults.length === 1
  ) {
    const p = projectViewResults[0];
    fetchForecastByProject(p.project_code);
    fetchProjectUnavailability(p.project_code);
  }
}, [projectViewResults, viewForecastTab]);


const handleSaveTaskEdit = async (taskId: number) => {
  if (!selectedProject || !taskModalDiscipline) return;

  try {
    setIsSavingTask(true);

    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=update_project_discipline_task";

    await fetchWithAuthV2(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_discipline_task_id: taskId,
        task_description: editTaskDescription,
        total_forecasted_hours: editTaskHours,
      }),
    });

    showToast("Task updated successfully.", "success");

    // refresh task data everywhere
    await fetchTasksByProject(selectedProject.project_code);
    const updated = await fetchDisciplineTasks(taskModalDiscipline);
    setModalTasks(updated);

    setEditingTaskId(null);
  } catch (err) {
    const msg =
      err instanceof Error
        ? err.message
        : "Error updating project discipline task.";
    showToast(msg, "error");
  } finally {
    setIsSavingTask(false);
  }
};


const fetchDisciplineTasks = async (disciplineCode: string) => {
  if (!selectedProject) return [];

  const allTasks = getCachedProjectTasks(selectedProject.project_code);

  // Filter tasks by discipline
  const records = Object.values(allTasks).filter(
    (t: any) => t.discipline === disciplineCode
  );

  setDisciplineTasks((prev) => ({
    ...prev,
    [disciplineCode]: records,
  }));

  return records;
};

const refreshTasksForProject = async () => {
  if (!selectedProject) return;
  await fetchTasksByProject(selectedProject.project_code);
};

const navigateToByProject = async (projectCode: string) => {
  // 1️⃣ Switch tabs
  setActiveTab(2);
  setViewForecastTab("byProject");

  // 2️⃣ Reset state
  setByProjectForecast(null);
  setProjectUnavailability({});
  setSelectedPMId("");
  setSearchValue("");

  // 3️⃣ Load forecast directly (bypass dropdown)
  await fetchForecastByProject(projectCode);
  await fetchProjectUnavailability(projectCode);
};



const navigateToProjectForecast = async (projectCode: string) => {
  const project = await getCachedProject(projectCode);
  if (!project) {
    showToast("Project not found.", "error");
    return;
  }

  // Switch to Manage Forecast tab
  setActiveTab(1);

  // Preselect project
  setSelectedProject(project);
  setProjectHours(Number(project.project_hours ?? 0));
  setProjectHoursSaved(true);

  // Reset editor state
  setSelectedDiscipline("");
  setTempDiscipline("");
  setStartDate("");
  setEndDate("");
  setProjectManager("");
  setDisciplineTotalHours("");
  setMondayDates([]);
  setHoursRows([]);

  // Load forecast + tasks
  await fetchTasksByProject(project.project_code);
  await fetchProjectForecastDetails(project.project_code);
};


useEffect(() => {
  if (!selectedManningId) {
    setManningUnavailability([]);
    return;
  }

  const fetchUnavailability = async () => {
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      `&action=get_manning_unavailability&manning_id=${encodeURIComponent(selectedManningId)}`;

    const resp = await fetchWithAuth(url);

    const raw: RawManningUnavailability[] =
      Array.isArray(resp?.records) ? resp.records : [];

    const normalized: ManningUnavailability[] = raw.map((r) => {
      const dayCount = r.days
        ? r.days.split(",").map(d => d.trim()).filter(Boolean).length
        : 0;

      return {
        weekdate: r.weekdate,
        hours: dayCount * 8, // ✅ 8 hours per day
      };
    });

    setManningUnavailability(normalized);
  };

  fetchUnavailability();
}, [selectedManningId]);


const handleAddTask = async () => {
  if (!selectedProject || !taskModalDiscipline) return;
  try{
      const url =
        "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
        "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
        "&action=add_project_discipline_task";

      await fetchWithAuthV2(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_code: selectedProject.project_code,
          discipline: taskModalDiscipline,
          task_description: taskDescription,
          total_forecasted_hours: taskHours,
        }),
      });

      showToast("Task added successfully.", "success");
      await fetchTasksByProject(selectedProject.project_code);

      // 🔁 Refresh BOTH states
      const updatedTasks = await fetchDisciplineTasks(taskModalDiscipline);
      setModalTasks(updatedTasks);
  }catch (err) {
      const msg = err instanceof Error ? err.message : "Error adding project discipline record for the forecast.";
      showToast(msg, "error");
  }finally{
      // Reset inputs
      setTaskDescription("");
      setTaskHours(0);
  }
};



// ===== View Forecasts API =====
const fetchForecastByManning = async (manningId: string) => {
  if (!manningId) return;

  setIsFetchingByManning(true);
  setByManningProjects([]); // optional: clears stale data

  try {
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      `&action=get_pdfmh_by_manning&manning_id=${encodeURIComponent(manningId)}`;

    const data = await fetchWithAuth(url);

    const projects: ManningProjectForecast[] = Array.isArray(data?.projects)
      ? data.projects
      : [];

    setByManningProjects(projects);
  } catch (err) {
    console.error("Error fetching By-Manning forecast", err);
    showToast("Failed to load forecast.", "error");
  } finally {
    setIsFetchingByManning(false);
  }
};



const getTaskDescription = (
  projectCode: string,
  taskId?: number
): string => {
  if (!taskId) return "";
  const tasks = getCachedProjectTasks(projectCode);
  return tasks[taskId]?.task_description ?? "Unknown Task";
};


const fetchProjectsByProjectView = async () => {
  if (!searchValue.trim()) {
    showToast("Please enter a value to search.", "error");
    return;
  }

  // 🔹 Project Code → immediate fetch
  if (projectViewSearchType === "byProjectCode") {
    await fetchForecastByProject(searchValue.trim());
    return;
  }

  // 🔹 Name / PM → show dropdown
  const url =
    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
    "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
    "&action=search_project" +
    `&search_type=${projectViewSearchType}` +
    `&search_value=${encodeURIComponent(searchValue)}`;

  const projects = await fetchWithAuth(url);
  setProjectViewResults(Array.isArray(projects) ? projects : []);
};


interface ProjectManningWeeks {
  manning_info_id: number | string;
  discipline: string; // ✅ ADD THIS
  weeks: {
    weekdate: string; // YYYY-MM-DD
    hours: number;
  }[];
}

const openTaskModal = async (disciplineCode: string) => {
  setTaskModalDiscipline(disciplineCode);
  setShowTaskModal(true);

  const tasks = await fetchDisciplineTasks(disciplineCode);
  setModalTasks(tasks ?? []);
};

const saveAbsences = async (
  manningInfoId: string,
  payload: AbsenceSavePayload[]
) => {
  if (!manningInfoId || payload.length === 0) return;

  try {
    setIsSavingAbsence(true);
    setAbsenceSaveSuccess(false);

    await fetchWithAuthV2(
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
        "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
        "&action=add_manning_unavailability",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          manning_info_id: manningInfoId,
          hoursDateArray: payload,
        }),
      }
    );

    // ✅ success confirmation
    setAbsenceSaveSuccess(true);

    // optional: auto-hide after 3s
    setTimeout(() => setAbsenceSaveSuccess(false), 3000);

  } catch (err) {
    console.error("Failed to save absences", err);
    alert("Failed to save absences. Please try again.");
  } finally {
    setIsSavingAbsence(false);
  }
};


const fetchForecastHoursByProject = async (projectCode: string) => {
  const url =
    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
    "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
    `&action=get_pdfmh_by_project` +
    `&project_code=${encodeURIComponent(projectCode)}`;

  const data = await fetchWithAuth(url);

  return Array.isArray(data?.records)
    ? (data.records as ProjectManningWeeks[])
    : [];
};

const fetchForecastByProjectGroup = async (projectGroupId: string) => {
  if (!projectGroupId) return;

  setIsFetchingByProjectGroup(true);
  setProjectGroupForecast(null);

  try {
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=get_pdfmh_by_project_group" +
      `&project_group_id=${encodeURIComponent(projectGroupId)}`;

    const resp = await fetchWithAuth(url);

    if (resp?.status === "success") {
      setProjectGroupForecast(resp.projects ?? []);
    } else {
      setProjectGroupForecast([]);
      showToast("No forecast found for this project group.", "info");
    }
  } catch (err) {
    console.error("Error fetching project group forecast", err);
    showToast("Failed to load project group forecast.", "error");
  } finally {
    setIsFetchingByProjectGroup(false);
  }
};


/**
 * Compute all MONDAYS between two YYYY-MM-DD dates (UTC-safe)
 */
const computeMondaysBetweenUTC = (start: string, end: string): string[] => {
  const result: string[] = [];

  let d = new Date(`${start}T00:00:00Z`);
  const endDate = new Date(`${end}T00:00:00Z`);

  // snap to Monday
  const day = d.getUTCDay(); // 0..6
  const diff = (day === 0 ? -6 : 1) - day;
  d.setUTCDate(d.getUTCDate() + diff);

  while (d <= endDate) {
    result.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 7);
  }

  return result;
};

const computeNextSixMonthMondays = () => {
  const start = new Date();
  const end = new Date();
  end.setMonth(end.getMonth() + 6);

  return computeMondaysBetweenUTC(
    start.toISOString().slice(0, 10),
    end.toISOString().slice(0, 10)
  );
};


  /* ========== API HELPERS ========== */
const handleGenerateForecastSheet = async () => {
  if (!selectedProject) {
    showToast("Please select a project.", "error");
    return;
  }
  if (!tempDiscipline) {
    showToast("Please select a discipline.", "error");
    return;
  }
  if (!startDate || !endDate) {
    showToast("Please select both start and end dates.", "error");
    return;
  }
  if (!projectManager) {
    showToast("Project Manager is required.", "error");
    return;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    showToast("Invalid date selection.", "error");
    return;
  }
  if (start > end) {
    showToast("Project Start Date cannot be after Project End Date.", "error");
    return;
  }

  if (disciplineTotalHours === '' || disciplineTotalHours <= 0) {
    showToast("Total Discipline Hours is required.", "error");
    return;
  }

  if (isGeneratingForecast) {
    return;
  }

  try {
    setIsGeneratingForecast(true);

    const newDiscipline = tempDiscipline;  // ★ Capture BEFORE clearing!

    // 1) Save discipline forecast
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=add_project_discipline_forecast";

    await fetchWithAuthV2(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        project_code: selectedProject.project_code,
        discipline: newDiscipline,
        project_start_date: startDate,
        project_end_date: endDate,
        project_manager_manning_id: Number(projectManager),
        total_discipline_hours: Number(disciplineTotalHours),
      }),
    });

    showToast("Project discipline forecast created successfully.", "success");

    // 2) Reset input fields for the NEXT entry
    setTempDiscipline("");
    setStartDate("");
    setEndDate("");
    setProjectManager("");
    setDisciplineTotalHours('');

    // 3) Compute weekdates for the NEW discipline
    const mondays = computeMondaysBetweenUTC(startDate, endDate);

    setMondayDatesByDiscipline((prev) => ({
      ...prev,
      [newDiscipline]: mondays,
    }));

    // 4) Initialize add-manning row for THIS discipline
    setHoursRows([
      {
        manning_info_id: "",
        hours: mondays.map(() => 0),
      },
    ]);

    // 5) Show the Add Manning section for this discipline
    setSelectedDiscipline(newDiscipline);
    setShowAddManningForDiscipline((prev) => ({
      ...prev,
      [newDiscipline]: true,
    }));

    // 6) Refresh bottom section
    await fetchProjectForecastDetails(selectedProject.project_code);
  } catch (err) {
      const msg = err instanceof Error ? err.message : "Error adding project discipline record for the forecast.";
      showToast(msg, "error");
  } finally {
    setIsGeneratingForecast(false);
  }
};

useEffect(() => {
  if (!selectedProject) return;
  fetchProjectForecastDetails(selectedProject.project_code);
}, [selectedProject?.project_code]);


const handleAddSingleHour = async (disciplineCode: string, rowIndex: number,colIndex: number) => {
  if (!selectedProject) {
    showToast("Please select a project first.", "error");
    return;
  }

  // 🔒 Capture weeks BEFORE refresh (critical)
  const weekDates = mondayDatesByDiscipline[disciplineCode] ?? [];
  if (!weekDates.length) {
    showToast("No weeks defined for this discipline.", "error");
    return;
  }

  const row = hoursRows[rowIndex];
  if (!row?.manning_info_id) {
    showToast("Please select a manning resource for this row.", "error");
    return;
  }

  const isoDate = weekDates[colIndex];
  const hours = row.hours?.[colIndex] ?? 0;


  // 🔒 Snapshot current editor row BEFORE async calls
  const preservedRow = {
    manning_info_id: row.manning_info_id,
    hours: [...row.hours],
  };

  try {
    // ✅ Save single cell
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=add_project_discipline_manning_hours";

    await fetchWithAuthV2(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_code: selectedProject.project_code,
        discipline: disciplineCode,
        manning_info_id: row.manning_info_id,
        project_discipline_task_id: selectedTaskByDiscipline[disciplineCode] || null,
        weekdate: isoDate,
        hours,
      }),
    });

    showToast(
      `Saved ${hours} hours for ${formatWeekLabel(isoDate)}.`,
      "success"
    );

    // 🔄 Refresh bottom section (authoritative)
    await fetchProjectForecastDetails(selectedProject.project_code);
    await refreshTasksForProject();
    await refreshDisciplineTasksState();

    // ✅ RESTORE editor row using PRE-SAVED values
    setHoursRows([
      {
        manning_info_id: preservedRow.manning_info_id,
        hours: preservedRow.hours,
      },
    ]);
  } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Error updating project forecast for this date.";
      showToast(msg, "error");
    }
};


// Save all hours for that Manning row for a discipline
const handleAddManningForecast = async (disciplineCode: string, rowIndex: number) => {
  if (!selectedProject) {
    showToast("Please select a project first.", "error");
    return;
  }

  const weekDates = mondayDatesByDiscipline[disciplineCode] ?? [];
  if (!weekDates.length) {
    showToast("No weeks defined for this discipline.", "error");
    return;
  }

  const row = hoursRows[rowIndex];
  if (!row?.manning_info_id) {
    showToast("Please select a manning resource for this row.", "error");
    return;
  }

  const selectedTaskId = selectedTaskByDiscipline[disciplineCode];
  if (!selectedTaskId) {
    showToast("Please select a task before saving forecast hours.", "error");
    return;
  }

  const hoursDateArray = weekDates.map((isoDate, idx) => ({
      date: isoDate,
      hours: row.hours?.[idx] ?? 0,
  }));

  try {
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=add_project_discipline_manning_hours";

    await fetchWithAuthV2(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        project_code: selectedProject.project_code,
        discipline: disciplineCode,
        manning_info_id: row.manning_info_id,
        project_discipline_task_id: selectedTaskByDiscipline[disciplineCode] || null,
        hoursDateArray,
      }),
    });

    showToast("Saved forecast for this manning resource.", "success");

    // Reset row with default hours for that discipline's weeks
    setHoursRows([
      {
        manning_info_id: "",
        hours: weekDates.map(() => 0),
      },
    ]);

    await fetchProjectForecastDetails(selectedProject.project_code);
    await refreshTasksForProject();
    await refreshDisciplineTasksState();
  } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Error updating project forecast for this date.";
      showToast(msg, "error");
    }
};

const uniqueLeaders = Array.from(
  new Set(
    manningList
      .map((m) => m.leader_name)
      .filter((x): x is string => Boolean(x))
  )
).sort();


const getAddedManningIdsForDiscipline = (disciplineCode: string): Set<string> => {
  const rows = projectForecastManningHours[disciplineCode] ?? [];
  return new Set(rows.map(r => String(r.manning_info_id)));
};


  /* ========== EDIT FROM BOTTOM → LOAD INTO TOP ========== */

  const handleEditManningForecast = (disciplineCode: string, manningId: string) => {
    if (!selectedProject) return;

    const forecast = projectForecasts.find((f) => f.discipline === disciplineCode);
    if (!forecast) return;

    const rowsForDiscipline = projectForecastManningHours[disciplineCode] || [];
    const thisManningRows = rowsForDiscipline.filter(
      (r) => String(r.manning_info_id) === String(manningId)
    );
    if (!thisManningRows.length) return;

    const allWeeks = Array.from(new Set(rowsForDiscipline.map((r) => r.weekdate))).sort();

    const hoursByWeek: Record<string, number> = {};
    thisManningRows.forEach((r) => {
      hoursByWeek[r.weekdate] = r.hours;
    });

    const startIso = forecast.project_start_date?.slice(0, 10);
    const endIso = forecast.project_end_date?.slice(0, 10);

    if (startIso) setStartDate(startIso);
    if (endIso) setEndDate(endIso);

    setTempDiscipline(disciplineCode);
    setSelectedDiscipline(disciplineCode);
    setProjectManager(String(forecast.project_manager_manning_id));
    setMondayDates(allWeeks);

    setHoursRows([
      {
        manning_info_id: String(manningId),
        hours: allWeeks.map((w) => hoursByWeek[w] ?? 0),
      },
    ]);

    setActiveTab(1);
    showToast('Loaded forecast into editor. Update hours and save.', 'info');
  };

  /* ========== USER HEADER ========== */
  const [username, setUsername] = useState('');
  const [showLogout, setShowLogout] = useState(false);

  useEffect(() => {
    const sessionData = localStorage.getItem('userToken');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        setUsername(parsed.username || '');
      } catch {
        console.error('Failed to parse session userToken');
      }
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    window.location.href = '/';
  };

  /* ========== PROJECT SEARCH ========== */

  const handleProjectSearch = async () => {
    if (!searchValue.trim()) {
      showToast('Please enter a value to search.', 'error');
      return;
    }

    setIsSearchingProject(true);

    const type = searchType === 'code' ? 'byProjectCode' : 'byProjectName';

    const url =
      'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==' +
      '&action=search_project' +
      `&search_type=${type}` +
      `&search_value=${encodeURIComponent(searchValue)}`;

    try {
      const data = await fetchWithAuth(url);

      if (!Array.isArray(data) || data.length === 0) {
        showToast('No projects found.', 'info');
        setSearchResults([]);
        return;
      }

      setSearchResults(data);
    } catch (err) {
      console.error('Search error:', err);
      showToast('Error searching for projects.', 'error');
    } finally{
      setIsSearchingProject(false);
    }
  };

  /* ========== BOTTOM SECTION RENDER HELPERS ========== */

  const buildManningDisplayData = (disciplineCode: string) => {
    const rows = projectForecastManningHours[disciplineCode] || [];
    if (!rows.length) {
      return {
        weeks: [] as string[],
        byManning: [] as {
          manning_info_id: string;
          hoursByWeek: Record<string, number>;
        }[],
      };
    }

    const weeks = Array.from(new Set(rows.map((r) => r.weekdate))).sort();

    const byManningMap = new Map<
      string,
      { manning_info_id: string; hoursByWeek: Record<string, number> }
    >();

    rows.forEach((r) => {
      const id = String(r.manning_info_id);
      if (!byManningMap.has(id)) {
        byManningMap.set(id, { manning_info_id: id, hoursByWeek: {} });
      }
      byManningMap.get(id)!.hoursByWeek[r.weekdate] = r.hours;
    });

    const byManning = Array.from(byManningMap.values());
    return { weeks, byManning };
  };

  /** Edit entire discipline forecast (load into editor) */
const handleEditDiscipline = (disciplineCode: string) => {
  const forecast = projectForecasts.find(f => f.discipline === disciplineCode);
  if (!forecast) return;

  // Load discipline into top editor
  setTempDiscipline(forecast.discipline);
  setSelectedDiscipline(forecast.discipline);
  setStartDate(forecast.project_start_date.slice(0, 10));
  setEndDate(forecast.project_end_date.slice(0, 10));
  setProjectManager(String(forecast.project_manager_manning_id));

  // Rebuild Monday list based on stored dates
  generateMondays(
    forecast.project_start_date.slice(0, 10),
    forecast.project_end_date.slice(0, 10)
  );

  // Load all manning rows for this discipline
  const rows = projectForecastManningHours[disciplineCode] || [];
  const weeks = Array.from(new Set(rows.map(r => r.weekdate))).sort();

  const hoursMap: Record<string, number> = {};
  rows.forEach(r => (hoursMap[r.weekdate] = r.hours));

  setHoursRows([
    {
      manning_info_id: "",
      hours: weeks.map(w => hoursMap[w] ?? 0)
    }
  ]);

  setActiveTab(1);
  showToast("Discipline loaded into editor.", "info");
};


const getAvailableDisciplines = () => {
  const used = new Set(projectForecasts.map((f) => f.discipline));
  return disciplines.filter((d) => !used.has(d.discipline_code));
};


/** Delete discipline forecast + all manning hours */
const handleDeleteDiscipline = async (disciplineCode: string) => {
  if (!selectedProject) return;

  //if (!window.confirm("Are you sure you want to delete this discipline forecast?")) {
    //return;
  //}

  try {
    const url =
      `https://manningserviceapp.azurewebsites.net/api/MainRoute` +
      `?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==` +
      `&action=delete_project_discipline_forecast` +
      `&project_code=${encodeURIComponent(selectedProject.project_code)}` +
      `&discipline_code=${encodeURIComponent(disciplineCode)}`;

    await fetchWithAuth(url);

    showToast("Discipline deleted successfully.", "success");

    // Refresh the bottom section
    await fetchProjectForecastDetails(selectedProject.project_code);
    await refreshTasksForProject();
    await refreshDisciplineTasksState();
  } catch (err) {
    console.error("Error deleting discipline:", err);
    showToast("Error deleting discipline", "error");
  }
};

const resetNewForecastState = () => {
  // Clear project selection
  setSelectedProject(null);

  // Clear discipline fields
  setTempDiscipline('');
  setSelectedDiscipline('');

  // Clear date fields
  setStartDate('');
  setEndDate('');

  // Clear project manager
  setProjectManager('');

  // Clear search
  setSearchValue('');
  setSearchResults([]);

  // Clear weekly row data
  setMondayDates([]);
  setHoursRows([]);

  setDisciplineTotalHours('');

  // Reset top section to blank
  setHoursRows([
    {
      manning_info_id: '',
      hours: [],
    },
  ]);

  showToast("New Forecast reset.", "info");
};

const handleSaveEditedBottomRow = async (disciplineCode: string, manningId: string) => {
  if (!selectedProject) return;

  const key = `${disciplineCode}_${manningId}`;
  const original = originalHoursByRow[key] || {};

  const rows = projectForecastManningHours[disciplineCode] || [];
  const thisManningRows = rows.filter(r => String(r.manning_info_id) === String(manningId));

  const changed = thisManningRows
    .map(r => {
      const wk = r.weekdate.slice(0,10);
      const newVal = Number(r.hours ?? 0);
      const oldVal = Number(original[wk] ?? 0);
      return { wk, newVal, oldVal };
    })
    .filter(x => x.newVal !== x.oldVal);

  if (changed.length === 0) {
    showToast("No changes to save.", "info");
    setEditingRow(null);
    return;
  }

  const hoursDateArray = changed.map(x => ({
    date: x.wk,
    hours: x.newVal
  }));

  try {
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=update_project_discipline_manning_hours";

    await fetchWithAuthV2(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_code: selectedProject.project_code,
        discipline: disciplineCode,
        manning_info_id: manningId,
        hoursDateArray,
      }),
    });

    showToast("Manning hours updated successfully.", "success");
    setEditingRow(null);

    await fetchProjectForecastDetails(selectedProject.project_code);
    await refreshTasksForProject();
    await refreshDisciplineTasksState();
  } catch (err) {
      const msg = err instanceof Error ? err.message : "Error updating project forecast for this date.";
      showToast(msg, "error");
  }
};


useEffect(() => {
  if (viewForecastTab === 'byManning' && selectedManningId) {
    fetchForecastByManning(selectedManningId);
  }
}, [viewForecastTab, selectedManningId]);



const handleSaveDisciplineInfo = async (disciplineCode: string) => {
  if (!selectedProject) return;

  try {
    /* --------------------------------------------------
       1) UPDATE DISCIPLINE-LEVEL FORECAST
    -------------------------------------------------- */
    const updateDisciplineUrl =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=update_project_discipline_forecast";

    await fetchWithAuthV2(updateDisciplineUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_code: selectedProject.project_code,
        discipline: disciplineCode,
        project_start_date: editDisciplineState.start,
        project_end_date: editDisciplineState.end,
        project_manager_manning_id: Number(editDisciplineState.pm),
        total_discipline_hours: Number(editDisciplineState.totalHours)
      }),
    });

    /* --------------------------------------------------
       2) COMPUTE NEW MONDAY LIST
    -------------------------------------------------- */
    const newWeeks = computeMondaysBetweenUTC(
      editDisciplineState.start,
      editDisciplineState.end
    );

    /* --------------------------------------------------
       3) UPDATE TOP (ADD-MANNING) ROW UI
    -------------------------------------------------- */
    setMondayDates(newWeeks);
    setHoursRows([
      {
        manning_info_id: "",
        hours: newWeeks.map(() => 0),
      },
    ]);

    /* --------------------------------------------------
       4) DETERMINE EXISTING vs NEW WEEKDATES
    -------------------------------------------------- */
    const rows = projectForecastManningHours[disciplineCode] || [];

    const existingWeeks = new Set(
      rows.map((r) => r.weekdate)
    );

    const addedWeeks = newWeeks.filter(
      (w) => !existingWeeks.has(w)
    );

    if (addedWeeks.length === 0) {
      showToast(
        "Discipline updated. No new weekdates added.",
        "info"
      );
      setEditingDiscipline(null);
      await fetchProjectForecastDetails(selectedProject.project_code);
      return;
    }

    if (
      editDisciplineState.totalHours === "" ||
      editDisciplineState.totalHours <= 0
    ) {
      showToast("Total Discipline Hours must be greater than 0.", "error");
      return;
    }

    /* --------------------------------------------------
       5) GROUP EXISTING ROWS BY MANNING + TASK
    -------------------------------------------------- */
    const grouped = new Map<
      string,
      {
        manningId: string;
        taskId: number;
      }
    >();

    rows.forEach((r) => {
      if (!r.project_discipline_task_id) return;

      const key = `${r.manning_info_id}_${r.project_discipline_task_id}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          manningId: String(r.manning_info_id),
          taskId: r.project_discipline_task_id,
        });
      }
    });

    /* --------------------------------------------------
       6) ADD ONLY NEW WEEKS (TASK-AWARE ADD API)
    -------------------------------------------------- */
    const addHoursUrl =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=add_project_discipline_manning_hours";

    for (const { manningId, taskId } of Array.from(grouped.values())) {
      const hoursDateArray = addedWeeks.map((week) => ({
        date: week,
        hours: 0,
      }));

      await fetchWithAuth(addHoursUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_code: selectedProject.project_code,
          discipline: disciplineCode,
          manning_info_id: manningId,
          project_discipline_task_id: taskId,
          hoursDateArray,
        }),
      });
    }

    /* --------------------------------------------------
       7) FINALIZE
    -------------------------------------------------- */
    showToast(
      "Discipline updated and new weeks added successfully.",
      "success"
    );

    setEditingDiscipline(null);
    await fetchProjectForecastDetails(selectedProject.project_code);

  } catch (err) {
      const msg = err instanceof Error ? err.message : "Error adding project discipline record for the forecast.";
      showToast(msg, "error");
  }
};



const handleSaveDisciplineInfo1 = async (disciplineCode: string) => {
  if (!selectedProject) return;

  try {
    // 1) Update discipline-level forecast
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=update_project_discipline_forecast";

    await fetchWithAuth(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_code: selectedProject.project_code,
        discipline: disciplineCode,
        project_start_date: editDisciplineState.start,
        project_end_date: editDisciplineState.end,
        project_manager_manning_id: Number(editDisciplineState.pm)
      })
    });

    // 2) Recompute Monday week list
    const newWeeks = computeMondaysBetweenUTC(
      editDisciplineState.start,
      editDisciplineState.end
    );

    //  UPDATE TOP ENTRY MANNING ROW (the add-manning row)
    setMondayDates(newWeeks);
    setHoursRows([
      {
        manning_info_id: "",
        hours: newWeeks.map(() => 0)  // default hours for new weeks
      }
    ]);

    // 3) Get all existing manning rows under this discipline
    const rows = projectForecastManningHours[disciplineCode] || [];
    const uniqueMannings = new Set(rows.map(r => String(r.manning_info_id)));

    // 4) For EACH Manning → Build updated hours array
    for (const manningId of Array.from(uniqueMannings)) {
      const existingRows = rows.filter(
        r => String(r.manning_info_id) === String(manningId)
      );

      const hoursMap: Record<string, number> = {};
      existingRows.forEach(r => (hoursMap[r.weekdate] = r.hours));

      const hoursDateArray = newWeeks.map(w => ({
        date: w,
        hours: hoursMap[w] ?? 0
      }));

      // 5) Bulk save using backend UPSERT
      const updateHoursUrl =
        "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
        "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
        "&action=update_project_discipline_manning_hours";

      await fetchWithAuth(updateHoursUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_code: selectedProject.project_code,
          discipline: disciplineCode,
          manning_info_id: manningId,
          hoursDateArray
        })
      });
    }

    showToast("Discipline and all weekdates updated successfully", "success");
    setEditingDiscipline(null);

    // 6) Refresh bottom section
    await fetchProjectForecastDetails(selectedProject.project_code);

  } catch (err) {
    console.error("Error updating discipline:", err);
    showToast("Error updating discipline info", "error");
  }
};

const computeMondaysBetween = (start: string, end: string) => {
  const result: string[] = [];
  let d = new Date(start);

  // Snap to Monday
  const day = d.getDay();
  const diffToMonday = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diffToMonday);

  const endObj = new Date(end);

  while (d <= endObj) {
    result.push(d.toISOString().split("T")[0]);
    d.setDate(d.getDate() + 7);
  }

  return result;
};

const fetchTasksByProject = async (projectCode: string) => {
  const url =
    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
    "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
    "&action=get_tasks_by_project" +
    `&project_code=${encodeURIComponent(projectCode)}`;

  const resp = await fetchWithAuth(url);
  const records = Array.isArray(resp?.records) ? resp.records : [];

  cacheProjectTasks(projectCode, records);
  return records;
};


const refreshDisciplineTasksState = async () => {
  if (!selectedProject) return;

  const allTasks = getCachedProjectTasks(selectedProject.project_code);

  const updated: Record<string, ProjectDisciplineTask[]> = {};

  Object.values(allTasks).forEach((t: any) => {
    if (!updated[t.discipline]) {
      updated[t.discipline] = [];
    }
    updated[t.discipline].push(t);
  });

  setDisciplineTasks(updated); // 🔥 THIS triggers re-render
};


/**
 * Delete ALL manning_hours for one manning under one discipline.
 * (One row in the table = multiple weekdates to delete)
 */
const handleDeleteManningHoursRow = async (disciplineCode: string, manningId: string) => {
  if (!selectedProject) {
    showToast("Please select a project first.", "error");
    return;
  }

  const rows = projectForecastManningHours[disciplineCode] || [];
  const thisManningRows = rows.filter(
    (r) => String(r.manning_info_id) === String(manningId)
  );

  if (thisManningRows.length === 0) {
    showToast("No hours found for this manning.", "error");
    return;
  }

  // Build deleteHoursArray
  const deleteHoursArray = thisManningRows.map((r) => ({
    project_code: selectedProject.project_code,
    discipline: disciplineCode,
    manning_info_id: Number(manningId),
    weekdate: r.weekdate,
  }));

  try {
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=delete_project_discipline_manning_hours";

    await fetchWithAuth(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deleteHoursArray }),
    });

    showToast("Manning hours deleted.", "success");

    // Refresh UI
    await fetchProjectForecastDetails(selectedProject.project_code);
    await refreshTasksForProject();
    await refreshDisciplineTasksState();
  } catch (err) {
    console.error("Error deleting manning hours:", err);
    showToast("Error deleting this manning entry.", "error");
  }
};

interface ManningByDiscipline {
  [discipline: string]: {
    weeks: string[];
    projects: {
      project_code: string;
      hoursByWeek: Record<string, number>;
    }[];
  };
}

const buildByManningDisciplineView = (
  records: DisciplineManningHourRow[]
): ManningByDiscipline => {
  const map: ManningByDiscipline = {};

  records.forEach((r) => {
    const discipline = r.discipline;
    const week = r.weekdate.slice(0, 10);

    if (!map[discipline]) {
      map[discipline] = {
        weeks: [],
        projects: [],
      };
    }

    // Track weeks
    if (!map[discipline].weeks.includes(week)) {
      map[discipline].weeks.push(week);
    }

    // Find or create project row
    let project = map[discipline].projects.find(
      (p) => p.project_code === r.project_code
    );

    if (!project) {
      project = {
        project_code: r.project_code,
        hoursByWeek: {},
      };
      map[discipline].projects.push(project);
    }

    project.hoursByWeek[week] = r.hours;
  });

  // Sort weeks
  Object.values(map).forEach((d) =>
    d.weeks.sort((a, b) => a.localeCompare(b))
  );

  return map;
};

interface ByProjectDisciplineView {
  [discipline: string]: {
    weeks: string[];
    mannings: {
      manning_info_id: string;
      hoursByWeek: Record<string, number>;
    }[];
  };
}

const buildByProjectDisciplineView = (
  records: ProjectManningWeeks[]
): ByProjectDisciplineView => {
  const map: ByProjectDisciplineView = {};

  records.forEach((r) => {
    const discipline = r.discipline;

    if (!map[discipline]) {
      map[discipline] = {
        weeks: [],
        mannings: [],
      };
    }

    // Collect weeks
    r.weeks.forEach((w) => {
      const week = w.weekdate.slice(0, 10);
      if (!map[discipline].weeks.includes(week)) {
        map[discipline].weeks.push(week);
      }
    });

    // Build hours map
    const hoursByWeek: Record<string, number> = {};
    r.weeks.forEach((w) => {
      hoursByWeek[w.weekdate.slice(0, 10)] = w.hours;
    });

    map[discipline].mannings.push({
      manning_info_id: String(r.manning_info_id),
      hoursByWeek,
    });
  });

  // Sort weeks
  Object.values(map).forEach((d) =>
    d.weeks.sort((a, b) => a.localeCompare(b))
  );

  return map;
};


useEffect(() => {
  const loadProjects = async () => {
    const codes = Object.values(byManningData)
      .flatMap(d => d.projects.map(p => p.project_code));

    const uniqueCodes = Array.from(new Set(codes));

    for (const code of uniqueCodes) {
      if (!projectNameCache[code]) {
        const p = await getCachedProject(code);
        if (p) {
          setProjectNameCache(prev => ({ ...prev, [code]: p }));
        }
      }
    }
  };

  loadProjects();
}, [byManningData]);

const mondayOf = (iso: string) => {
  const d = new Date(iso);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
};

const groupAbsenceByWeek = (dates: string[]) => {
  const map: Record<string, number> = {};

  dates.forEach(date => {
    const monday = mondayOf(date);
    map[monday] = (map[monday] ?? 0) + 8; // 8 hrs per day
  });

  return Object.entries(map).map(([weekdate, hours]) => ({
    weekdate,
    hours,
  }));
};

/**
 * Convert a YYYY-MM-DD to the MONDAY of that week (YYYY-MM-DD)
 * UTC-only, no timezone drift possible
 */
const getMondayWeekdate = (isoDate: string): string => {
  const d = new Date(`${isoDate}T00:00:00Z`);

  // 0=Sun, 1=Mon, ..., 6=Sat (UTC)
  const day = d.getUTCDay();

  // Shift date to Monday
  const diffToMonday = (day === 0 ? -6 : 1) - day;
  d.setUTCDate(d.getUTCDate() + diffToMonday);

  return d.toISOString().slice(0, 10);
};


const fetchForecastByProject = async (projectCode: string) => {
  if (!projectCode) return;

  const url =
    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
    "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
    "&action=get_pdfmh_by_project" +
    `&project_code=${encodeURIComponent(projectCode)}`;

  const data = await fetchWithAuth(url);

  if (data?.status === "success") {
    setByProjectForecast(data.project);
  } else {
    setByProjectForecast(null);
    showToast("No forecast found for this project.", "info");
  }
};

const loadProjectManagers = async () => {
  let allManning: Manning[] = [];

  const cached = localStorage.getItem("all_manning");
  if (cached) {
    allManning = JSON.parse(cached);
  } else {
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=get_all_manning_pros";

    allManning = await fetchWithAuth(url);
    localStorage.setItem("all_manning", JSON.stringify(allManning));
  }

  setPmList(
    allManning.filter(
      (m) => m.title?.toLowerCase() === "project manager"
    )
  );
};

useEffect(() => {
  if (projectViewSearchType === "byProjectPM") {
    loadProjectManagers();
  }
}, [projectViewSearchType]);

const fetchProjectsByPM = async (pmId: string) => {
  if (!pmId) return;

  setIsFetchingByProjectPM(true);
  setPmProjects(null);

  try {
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      `&action=get_pdfmh_by_project_pm&manning_id=${encodeURIComponent(pmId)}`;

    const data = await fetchWithAuth(url);
    setPmProjects(data?.project ?? null);
  } catch (err) {
    console.error(err);
    showToast("Failed to load projects for Project Manager.", "error");
  } finally {
    setIsFetchingByProjectPM(false);
  }
};

const fetchProjectUnavailability = async (projectCode: string) => {
  const url =
    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
    "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
    `&action=get_manning_unavailability_by_project&project_code=${encodeURIComponent(
      projectCode
    )}`;

  const resp = await fetchWithAuth(url);

  const map: Record<string, ManningUnavailability[]> = {};

  resp.records?.forEach((m: any) => {
    const normalized = m.records.map((r: any) => ({
      weekdate: r.weekdate,
      hours: r.days
        ? r.days.split(",").filter(Boolean).length * 8
        : 0,
    }));

    map[String(m.manning_info_id)] = normalized;
  });

  setProjectUnavailability(map);
};

useEffect(() => {
  const projectCode =
    pmProjects?.project_code ??
    byProjectForecast?.project_code;

  if (!projectCode) {
    setProjectUnavailability({});
    return;
  }

  setProjectUnavailability({});
  fetchProjectUnavailability(projectCode);
}, [pmProjects, byProjectForecast]);



const getCachedProject = async (projectCode: string): Promise<Project | null> => {
  const key = `project_${projectCode}`;
  const cached = localStorage.getItem(key);

  if (cached) {
    return JSON.parse(cached);
  }

  try {
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      `&action=get_project&project_code=${encodeURIComponent(projectCode)}`;

    const project = await fetchWithAuth(url);
    localStorage.setItem(key, JSON.stringify(project));
    return project;
  } catch (err) {
    console.error("Error fetching project", projectCode, err);
    return null;
  }
};

const TOTAL_HOURS_KEY = (manningId: string) => `total_manning_hours_${manningId}`;

const fetchAndCacheTotalHoursByWeek = async (manningId: string) => {
  const url =
    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
    "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
    "&action=get_total_manning_hours_by_week" +
    `&manning_id=${encodeURIComponent(manningId)}`;

  const resp = await fetchWithAuth(url);

  /**
   * Expected shape:
   * [{ weekdate: 'YYYY-MM-DD', total_hours: number }]
   */
  const map: Record<string, number> = {};
  const records: TotalHoursByWeekRecord[] =
    Array.isArray(resp?.records) ? resp.records : [];

  records.forEach((r: TotalHoursByWeekRecord) => {
    map[r.weekdate.slice(0, 10)] = r.total_hours;
  });

  localStorage.setItem(TOTAL_HOURS_KEY(manningId), JSON.stringify(map));
  return map;
};

const getCachedTotalHours = (manningId: string): Record<string, number> => {
  const raw = localStorage.getItem(TOTAL_HOURS_KEY(manningId));
  return raw ? JSON.parse(raw) : {};
};

const clearCachedTotalHours = (manningId: string) => {
  localStorage.removeItem(TOTAL_HOURS_KEY(manningId));
};

const fetchProjectGroups = async () => {
  try{
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=get_project_groups";

    const resp = await fetchWithAuthV2(url);
    setProjectGroups(Array.isArray(resp?.message.records) ? resp.message.records : []);
  }catch(err){
    const msg = err instanceof Error ? err.message : "Error fetching project groups.";
    showToast(msg, "error");
  }
};

const fetchProjectsByGroup = async (projectGroupId: string) => {
  const url =
    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
    "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
    "&action=get_projects_by_project_group" +
    `&project_group_id=${encodeURIComponent(projectGroupId)}`;

  const resp = await fetchWithAuth(url);
  setProjectGroupProjects(Array.isArray(resp?.records) ? resp.records : []);
};

const saveProjectGroup = async () => {
  if (!newProjectGroupDescription || !newProjectGroupProjectCode) {
    showToast("Project Group Description and Project Code are required.", "error");
    return;
  }

  try {
    setIsSavingProjectGroup(true);

    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=add_project_group";

    await fetchWithAuthV2(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_group_description: newProjectGroupDescription,
        project_code: newProjectGroupProjectCode,
      }),
    });

    showToast("Project Group created successfully.", "success");

    setShowProjectGroupModal(false);
    setNewProjectGroupDescription("");
    setNewProjectGroupProjectCode("");

    await fetchProjectGroups();
  } catch (err) {
    const msg =
      err instanceof Error
        ? err.message
        : "Error adding project group.";
    showToast(msg, "error");
  } finally {
    setIsSavingProjectGroup(false);
  }
};

useEffect(() => {
  if (viewForecastTab === "byProjectGroup") {
    fetchProjectGroups();
  }
}, [viewForecastTab]);

const selectedProjectGroup = projectGroups.find(
  g => String(g.project_group_id) === String(selectedProjectGroupId)
);

const openEditProjectGroupModal = async (group: ProjectGroup) => {
  setEditingProjectGroup(group);
  setEditProjectGroupDescription(group.project_group_description);
  setShowEditProjectGroupModal(true);

  try {
    const url =
      "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=get_projects_by_project_group" +
      `&project_group_id=${group.project_group_id}`;

    const resp = await fetchWithAuth(url);

    const records =
      resp?.records ??
      resp?.message?.projects ??
      [];
    
    setEditProjectGroupProjects(Array.isArray(records) ? records : []);
  } catch (err) {
    console.error(err);
    showToast("Failed to load project group details.", "error");
    setEditProjectGroupProjects([]);
  }
};


const updateProjectGroupDescription = async () => {
  if (!editingProjectGroup) return;

  await fetchWithAuthV2(
    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=update_project_group",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_group_id: editingProjectGroup.project_group_id,
        project_group_description: editProjectGroupDescription,
      }),
    }
  );

  showToast("Project Group updated.", "success");
  fetchProjectGroups();
};

const deleteProjectFromGroup = async (projectGroupProjectId: number) => {
  if (!editingProjectGroup) return;

  await fetchWithAuthV2(
    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=delete_project_group_project" +
      `&project_group_project_id=${projectGroupProjectId}`
  );

  showToast("Project removed from group.", "success");

  // 🔁 refresh list from server (authoritative)
  openEditProjectGroupModal(editingProjectGroup);
};


const addProjectToGroup = async () => {
  if (!editingProjectGroup || !newProjectCodeToAdd.trim()) return;

  await fetchWithAuthV2(
    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
      "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
      "&action=add_project_group_project",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_group_id: editingProjectGroup.project_group_id,
        project_code: newProjectCodeToAdd,
      }),
    }
  );

  showToast("Project added to group.", "success");
  setNewProjectCodeToAdd("");
  openEditProjectGroupModal(editingProjectGroup); // refresh list
};

const handleCloseEditProjectGroupModal = async () => {
  setShowEditProjectGroupModal(false);

  // Refresh project group list
  await fetchProjectGroups();

  // If a group is selected, refresh its projects
  if (selectedProjectGroupId) {
    await fetchForecastByProjectGroup(selectedProjectGroupId);
  }
};
const exportForecastToPDF = async () => {
  let targetRef: React.RefObject<HTMLDivElement>;
  let filename = "forecast";

  switch (viewForecastTab) {
    case "byProjectGroup":
      targetRef = projectGroupPdfRef;
      filename = selectedProjectGroup?.project_group_description || "project_group";
      break;

    case "byManning": {
      targetRef = byManningPdfRef;

      const manning = manningList.find(
        (m) => String(m.manning_info_id) === String(selectedManningId)
      );

      filename = manning?.name || "by_manning";
      break;
    }


    case "byProject":
      targetRef = byProjectPdfRef;
      filename = selectedProject?.project_code || "by_project";
      break;

    default:
      showToast("Nothing to export.", "error");
      return;
  }

  if (!targetRef.current) {
    showToast("Nothing to export.", "error");
    return;
  }

  const canvas = await html2canvas(targetRef.current, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
  });

  const imgData = canvas.toDataURL("image/png");

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "px",
    format: [canvas.width, canvas.height],
  });

  pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
  pdf.save(`${filename.replace(/\s+/g, "_").toLowerCase()}_forecast.pdf`);
};


  return (
    <div className="landing-container">
      {/* HEADER */}
      <header className="landing-header">
        <img src={logo} alt="Design Group Logo" className="landing-logo" />
        <h1 className="landing-title">Staffing Portal - Project</h1>

        <div
          className="user-icon-container"
          onMouseEnter={() => setShowLogout(true)}
          onMouseLeave={() => setShowLogout(false)}
        >
          <span className="username-text">{username}</span>
          <img src={userIcon} alt="User Icon" className="user-icon" />
          {showLogout && (
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          )}
        </div>
      </header>

      {/* SIDE NAV */}
      <Navigation
        regions={regions}
        expandedRegion={regionCode || null}
        selectedSubLink="project"
        toggleRegion={() => {}}
        setSelectedSubLink={() => {}}
      />

      {/* MAIN */}
      <main className="landing-main">
        {toast && <div className={`toast toast-${toast.type}`}>{toast.message}</div>}

        {!regionCode ? (
          <h2>Please select a region to view project details.</h2>
        ) : (
          <div className="project-wrapper">
            <h2 className="project-title">Project Forecast Management</h2>

            {/* TABS */}
            <div className="tabs-container">
            <button
              className={activeTab === 2 ? 'project-tab-button project-tab-button-selected' : 'project-tab-button'}
              onClick={() => setActiveTab(2)}
            >
              View Forecasts
            </button>


              <button
                className={activeTab === 1 ? 'project-tab-button project-tab-button-selected' : 'project-tab-button'}
                onClick={() => {
                   setActiveTab(1);
                }}
              >
                Manage Forecast
              </button>

            <button
              className="help-link"
              onClick={async () => {
                try {
                  const res = await fetchWithAuthV2(
                    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
                    "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_help_video_url"
                  );
  
                  setVideoUrl(res.message.video_url);
                  setShowHelpVideo(true);
                } catch(err) {
                  const msg =
                    err instanceof Error
                      ? err.message
                      : "Error updating project discipline task.";
                  showToast(msg, "error");
                }
              }}
            >
              ❓ Help
            </button>
            </div>

            <div className="tab-content scrollable-tab">
              {/* EXISTING FORECASTS TAB (kept, but empty of data) */}
{activeTab === 2 && (
  <div className="new-forecast-container">

    {/* SUB TABS */}
    <div className="tabs-container">
      <button
        className={viewForecastTab === 'byManning'
          ? 'project-tab-button project-tab-button-selected'
          : 'project-tab-button'}
        onClick={() => setViewForecastTab('byManning')}
      >
        By Manning
      </button>

      <button
        className={viewForecastTab === 'byProject'
          ? 'project-tab-button project-tab-button-selected'
          : 'project-tab-button'}
        onClick={() => setViewForecastTab('byProject')}
      >
        By Project
      </button>

      <button
        className={viewForecastTab === 'byProjectGroup'
          ? 'project-tab-button project-tab-button-selected'
          : 'project-tab-button'}
        onClick={() => setViewForecastTab('byProjectGroup')}
      >
        By Project Group
      </button>
    </div>

    {/* ================= BY MANNING ================= */}
{viewForecastTab === "byManning" && (
  <div ref={byManningPdfRef} className="by-manning-container">

    {/* HEADER */}
    <div className="by-manning-filter">
      <div className="by-manning-instruction">
        Select a professional to view the forecast:
      </div>

      <select
        value={selectedManningId}
        onChange={(e) => setSelectedManningId(e.target.value)}
      >
        <option value="">Select Manning</option>

        {isLoadingForecastManning && (
          <option disabled>Loading…</option>
        )}

        {!isLoadingForecastManning &&
          forecastManningList.map((m) => (
            <option key={m.manning_info_id} value={m.manning_info_id}>
              {m.manning_name}
            </option>
          ))}
      </select>

      {!isFetchingByManning &&
        Array.isArray(byManningProjects) &&
        byManningProjects.length > 0 && (
          <button
            className="export-pdf-icon-btn"
            title="Download Forecast as PDF"
            onClick={exportForecastToPDF}
          >
            <img
              src={pdfIcon}
              alt="Export PDF"
              className="bymanning-export-pdf-icon"
            />
          </button>
      )}

    </div>

    {/* RESULTS */}
    {/* LOADING STATE */}
    {isFetchingByManning && (
      <div className="by-manning-loading">
        Processing your request. Please wait…
      </div>
    )}

    {!isFetchingByManning && Array.isArray(byManningProjects) && byManningProjects.map((project) => (

      <div key={project.project_code} className="by-manning-project-block">
        {/* PROJECT HEADER */}
        <div className={`by-manning-project-header ${project.opportunity_flag === 1 ? "opportunity" : ""}`}>
          <div className="project-title">
            <strong>{project.project_name}</strong>
          </div>

          <div className="project-meta">
            <span
              className="project-code-link"
              title="View project forecast"
              onClick={() => navigateToByProject(project.project_code)}
            >
              {project.project_code}
            </span>

            <span className="project-hours-badge">
              {project.project_hours} hrs
            </span>
          </div>
        </div>


        {Object.values(project.disciplines ?? {}).map((d) => (
          <div key={d.discipline} className="by-manning-discipline-block">

            {/* DISCIPLINE HEADER */}
            <div className="by-manning-discipline-header">
              {getDisciplineName(d.discipline)} — {d.total_discipline_hours} hrs
            </div>

            {/* WEEK GRID */}
            <div className="by-manning-week-grid">

              {/* WEEK HEADERS */}
              <div className="week-row header">
                {d.weekly_hours.map((w) => (
                  <div key={w.weekdate} className="week-cell">
                    {formatWeekLabel(w.weekdate)}
                  </div>
                ))}
              </div>

              {/* HOURS */}
              <div className="week-row hours">
                {d.weekly_hours.map((w) => (
                  <div key={w.weekdate} className="week-cell">
                    {w.hours}
                  </div>
                ))}
              </div>

            </div>
          </div>
        ))}

      </div>
    ))}

    {/* ================= UNAVAILABILITY ================= */}
    {manningUnavailability.length > 0 && (
      <div className="by-manning-project-block">

        <div className="by-manning-project-header unavailability-header">
          <strong>Unavailability (PTO, Training etc.)</strong>
        </div>

        <div className="by-manning-week-grid">

          {/* WEEK HEADERS */}
          <div className="week-row header">
            {manningUnavailability.map((u) => (
              <div key={u.weekdate} className="week-cell">
                {formatWeekLabel(u.weekdate)}
              </div>
            ))}
          </div>

          {/* HOURS */}
          <div className="week-row hours">
            {manningUnavailability.map((u) => (
              <div key={u.weekdate} className="week-cell unavailable">
                {u.hours}
              </div>
            ))}
          </div>

        </div>
      </div>
    )}

    {Array.isArray(byManningProjects) && byManningProjects.length > 0 && (
      <div className="forecast-legend">
        <div className="legend-item">
          <span className="legend-bar project" />
          <span>Projects</span>
        </div>

        <div className="legend-item">
          <span className="legend-bar opportunity" />
          <span>Proposals (Opportunities)</span>
        </div>
      </div>
    )}
  </div>
)}


    {/* ================= BY PROJECT ================= */}
{/* ================= BY PROJECT ================= */}
{viewForecastTab === "byProject" && (
  <div ref={byProjectPdfRef} className="by-manning-container">
    {/* ================= SEARCH CONTROLS ================= */}
 <div className="by-manning-filter">
      <div className="by-manning-instruction">
        Select a project to view the forecast:
      </div>

      <select
        className="project-results-dropdown"
        value={byProjectForecast?.project_code ?? ""}
        disabled={isLoadingByProject}
        onChange={async (e) => {
          const projectCode = e.target.value;

          setByProjectForecast(null);
          setProjectUnavailability({});

          if (!projectCode) return;

          await fetchForecastByProject(projectCode);
          await fetchProjectUnavailability(projectCode);
        }}
      >
        <option value="">
          {isLoadingByProject ? "Loading projects…" : "Select Project"}
        </option>

        {projectViewResults.map((p) => (
          <option key={p.project_code} value={p.project_code}>
            {p.project_code} — {p.project_name}
          </option>
        ))}
      </select>
    </div>

    {/* ================= LOADING (PM MODE) ================= */}
    {isFetchingByProjectPM && (
      <div className="by-manning-loading">Processing forecast…</div>
    )}

    {/* ================= FORECAST DISPLAY ================= */}
    {(() => {
      // PM mode uses pmProjects; code/name uses byProjectForecast
      const project = pmProjects ?? byProjectForecast;

      if (!project) return null;

      return (
        <div className="by-manning-project-block">
          {/* PROJECT HEADER - PDF*/}

          <div className={`by-manning-project-header ${project.opportunity_flag === 1 ? "opportunity" : ""}`}>
            <div className="project-title">
              <strong>{project.project_name}</strong>
            </div>

            <div className="project-meta">
              <span className="project-code-no-link">{project.project_code}</span>

              <span className="project-hours-badge">
                {project.project_hours} hrs
              </span>
            <button
              className="export-pdf-icon-btn"
              title="Download Forecast as PDF"
              onClick={exportForecastToPDF}
            >
              <img
                src={pdfIcon}
                alt="Export PDF"
                className="byproject-export-pdf-icon"
              />
            </button>
            </div>

          </div>

          {/* DISCIPLINES */}
          {Array.isArray(project.disciplines) && (() => {
            // 🔹 Build UNION of all weekdates across all disciplines/mannings
            const headerWeekdates = Array.from(
              new Set(
                project.disciplines.flatMap((d) =>
                  (d.mannings ?? []).flatMap((m) =>
                    (m.weekly_hours ?? []).map((w) => w.weekdate)
                  )
                )
              )
            ).sort(
              (a, b) => new Date(a).getTime() - new Date(b).getTime()
            );

            return project.disciplines.map((d, disciplineIndex) => (
              <div key={d.discipline} className="by-manning-discipline-block">
                {/* DISCIPLINE HEADER */}
                <div className="by-manning-discipline-header">
                  {getDisciplineName(d.discipline)} — {d.total_discipline_hours} hrs
                </div>

                <div className="by-manning-week-grid">
                  {/* 🔹 WEEK HEADER (ONLY ONCE) */}
                  {disciplineIndex === 0 && (
                    <div className="week-row header">
                      <div className="week-cell manning-name">Manning</div>
                      {headerWeekdates.map((wd) => (
                        <div key={wd} className="week-cell">
                          {formatWeekLabel(wd)}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 🔹 HOURS ROWS (ALIGNED TO HEADER) */}
                  {Array.isArray(d.mannings) &&
                    d.mannings.map((m) => {
                      // Map weekdate -> hours
                      const hoursByWeekdate = new Map(
                        (m.weekly_hours ?? []).map((w) => [w.weekdate, w.hours])
                      );

                      return (
                        <div key={m.manning_info_id} className="week-row hours">
                          <div className="week-cell manning-name">
                            {m.manning_name}
                          </div>

                          {headerWeekdates.map((wd) => (
                            <div key={wd} className="week-cell">
                              {hoursByWeekdate.get(wd) ?? 0}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                </div>
              </div>
            ));
          })()}



          {/* ================= UNAVAILABILITY (BOTTOM) ================= */}
          {Object.keys(projectUnavailability).length > 0 && (
            <div className="by-manning-project-block">
              <div className="by-manning-project-header unavailability-header">
                <strong>Unavailability (PTO, Training etc.)</strong>
              </div>

              {Object.entries(projectUnavailability).map(
                ([manningId, records]) => {
                  const manningName = getManningNameById(manningId);

                  return (
                    <div key={manningId} className="by-manning-week-grid">
                      {/* Header row: Manning + Weeks */}
                      <div className="week-row header">
                        <div className="week-cell manning-name">
                          {manningName}
                        </div>

                        {records.map((u) => (
                          <div key={u.weekdate} className="week-cell">
                            {formatWeekLabel(u.weekdate)}
                          </div>
                        ))}
                      </div>

                      {/* Hours row */}
                      <div className="week-row hours">
                        <div className="week-cell" />
                        {records.map((u) => (
                          <div key={u.weekdate} className="week-cell unavailable">
                            {u.hours}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          )}
        </div>
      );
    })()}

      <div className="forecast-legend">
        <div className="legend-item">
          <span className="legend-bar project" />
          <span>Projects</span>
        </div>

        <div className="legend-item">
          <span className="legend-bar opportunity" />
          <span>Proposals (Opportunities)</span>
        </div>
      </div>

  </div>
)}

    {/* ================= BY PROJECT GROUP ================= */}
    {viewForecastTab === "byProjectGroup" && (
      <div className="by-manning-container" ref={projectGroupPdfRef}>

        {/* HEADER */}
        <div className="by-manning-filter">
          <div className='by-manning-instruction'>Select a Project Group to view the forecast:</div>

          <select
            className="project-select"
            value={selectedProjectGroupId}
            onChange={(e) => {
              const value = e.target.value;

              if (value === "__ADD_NEW__") {
                setShowProjectGroupModal(true);
                return;
              }

              setSelectedProjectGroupId(value);
              fetchForecastByProjectGroup(value);
            }}
          >
            <option value="">Select Project Group</option>

            {projectGroups.map((g) => (
              <option key={g.project_group_id} value={g.project_group_id}>
                {g.project_group_description}
              </option>
            ))}

            <option value="__ADD_NEW__">➕ Add New Project Group</option>
          </select>
        </div>

        {/* LOADING */}
        {isFetchingByProjectGroup && (
          <div className="by-manning-loading">Processing forecast…</div>
        )}

        {selectedProjectGroup && (
        <div className="project-group-page-header">
          <div className="project-group-title-row">

            {/* LEFT SIDE */}
            <div className="project-group-title-left">
              <span className="project-group-title-label">Project Group:</span>

              <button
                className="project-group-title-link"
                title="Click to add or remove projects from this Project Group"
                onClick={() => openEditProjectGroupModal(selectedProjectGroup)}
              >
                {selectedProjectGroup.project_group_description}
                <span className="edit-icon">✎</span>
              </button>
            </div>

            {/* RIGHT SIDE — PDF EXPORT */}
            <button
              className="export-pdf-icon-btn"
              title="Download Forecast as PDF"
              onClick={exportForecastToPDF}
            >
              <img
                src={pdfIcon}
                alt="Export PDF"
                className="export-pdf-icon"
              />
            </button>
          </div>
        </div>

        )}

        {/* PROJECT GROUP FORECAST */}
        {!isFetchingByProjectGroup &&
          Array.isArray(projectGroupForecast) &&
          projectGroupForecast.map((project) => (
            <div key={project.project_code} className="by-manning-project-block">

              {/* PROJECT HEADER */}
              <div className={`by-manning-project-header ${project.opportunity_flag === 1 ? "opportunity" : ""}`}>
                <div className="project-title">
                  <strong>{project.project_name}</strong>
                </div>

                <div className="project-meta">
                  <span className="project-code-no-link">
                    {project.project_code}
                  </span>

                  <span className="project-hours-badge">
                    {project.project_hours} hrs
                  </span>
                </div>
              </div>

              {/* DISCIPLINES */}
              {Array.isArray(project.disciplines) &&
                project.disciplines.map((d: any) => (
                  <div key={d.discipline} className="by-manning-discipline-block">

                    {/* DISCIPLINE HEADER */}
                    <div className="by-manning-discipline-header">
                      {getDisciplineName(d.discipline)} — {d.total_discipline_hours} hrs
                    </div>

                    {/* EACH MANNING */}
                    {Array.isArray(d.mannings) &&
                      d.mannings.map((m: any) => (
                        <div key={m.manning_info_id} className="by-manning-week-grid">

                          {/* HEADER ROW */}
                          <div className="week-row header">
                            <div className="week-cell manning-name">
                              {m.manning_name}
                            </div>

                            {m.weekly_hours.map((w: any) => (
                              <div key={w.weekdate} className="week-cell">
                                {formatWeekLabel(w.weekdate)}
                              </div>
                            ))}
                          </div>

                          {/* HOURS ROW */}
                          <div className="week-row hours">
                            <div className="week-cell" />
                            {m.weekly_hours.map((w: any) => (
                              <div key={w.weekdate} className="week-cell">
                                {w.hours}
                              </div>
                            ))}
                          </div>

                        </div>
                      ))}
                  </div>
                ))}
            </div>
          ))}
      </div>
    )}
  </div>
)}


              {/* NEW FORECAST TAB */}
              {activeTab === 1 && (
                <div className="new-forecast-container">
                  {/* PROJECT SELECT */}

                  <div className="project-search-row">
                    {/* Search Type */}
                    <select
                      className="project-select"
                      value={searchType}
                      onChange={(e) =>
                        setSearchType(e.target.value as 'code' | 'name')
                      }
                    >
                      <option value="code">Search by Project/Opportunity Code</option>
                      <option value="name">Search by Project/Opportunity Name</option>
                    </select>

                    {/* Search Input */}
                    <input
                      className="project-search-input"
                      type="text"
                      placeholder={
                        searchType === 'code'
                          ? 'Enter project code'
                          : 'Enter project name'
                      }
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                    />

                    {/* Search Button */}

                    <button
                      className="project-search-button"
                      onClick={handleProjectSearch}
                      disabled={isSearchingProject}
                    >
                      {isSearchingProject ? "Searching..." : "Search"}
                    </button>

                    {/* Search Results Dropdown */}
                    <button
                      className="icon-btn reset"
                      title="Reset Forecast"
                      onClick={() => {
                        resetNewForecastState();
                      }}
                    >
                      🔄
                    </button>

                  </div>
                  {/* TOTAL PROJECT HOURS */}
                  {searchResults.length > 0 && (
                    <div className="project-select-hours-row">
                      {/* PROJECT SELECT */}
                      <div className="field-block" style={{ minWidth: 320 }}>
                        <label>Select Project</label>
                        <select
                          className="project-results-dropdown"
                          value={selectedProject?.project_code ?? ""}
                          onChange={async (e) => {
                            const projectCode = e.target.value;
                            const selected = searchResults.find(
                              (p) => p.project_code === projectCode
                            );
                            if (!selected) return;

                            const hours = Number(selected.project_hours ?? 0);

                            setSelectedProject(selected);
                            setProjectHours(hours);
                            setProjectHoursSaved(hours > 0);

                            if (hours === 0) {
                              showToast("Enter the total Project hours to continue", "info");
                            }

                            await fetchTasksByProject(selected.project_code);

                            // Reset editor state
                            setSelectedDiscipline('');
                            setMondayDates([]);
                            setHoursRows([]);
                            setStartDate('');
                            setEndDate('');
                            setTempDiscipline('');
                            setProjectManager('');
                            setDisciplineTotalHours('');

                            await fetchProjectForecastDetails(selected.project_code);
                          }}
                        >
                          <option value="">Select Project</option>
                          {searchResults.map((p) => (
                            <option key={p.project_code} value={p.project_code}>
                              {p.project_code} — {p.project_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* TOTAL PROJECT HOURS */}
                      {selectedProject && (
                        <div className="field-block" style={{ maxWidth: 260 }}>
                          <label>Total Project Hours</label>

                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input
                              type="number"
                              min={0}
                              value={projectHours}
                              onChange={(e) => {
                                setProjectHours(Number(e.target.value));
                                setProjectHoursSaved(false);
                              }}
                            />

                            <button
                              className="icon-btn save"
                              title="Save Project Hours"
                              onClick={async () => {
                                if (!projectHours || projectHours <= 0) {
                                  showToast("Enter the total Project hours to continue", "info");
                                  return;
                                }

                                try {
                                  const url =
                                    "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
                                    "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
                                    "&action=update_project_hours";

                                  await fetchWithAuth(url, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({
                                      project_code: selectedProject.project_code,
                                      project_hours: projectHours,
                                    }),
                                  });

                                  showToast("Project hours saved successfully.", "success");
                                  setProjectHoursSaved(true);
                                } catch (err) {
                                  showToast("Error saving project hours.", "error");
                                }
                              }}
                            >
                              ✓
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {selectedProject && projectHoursSaved && (
                    <>
                      {/* DISCIPLINE + DATES + PM */}
                      <div className="discipline-date-section">
                        {/* Discipline */}
                        <div className="field-block">
                          <label>Discipline</label>
                          <select
                            className="project-select"
                            value={tempDiscipline}
                            onChange={(e) => setTempDiscipline(e.target.value)}
                          >
                            <option value="">Select Discipline</option>
                            {getAvailableDisciplines().map((d) => (
                              <option
                                key={d.discipline_code}
                                value={d.discipline_code}
                              >
                                {d.discipline_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Start Date */}
                        <div className="field-block date-field">
                          <label>Start Date</label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                        </div>

                        {/* End Date */}
                        <div className="field-block date-field">
                          <label>End Date</label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                          />
                        </div>

                        {/* Project Manager */}
                        <div className="field-block">
                          <label>Project Manager</label>
                          <select
                            className="project-select"
                            value={projectManager}
                            onChange={(e) => setProjectManager(e.target.value)}
                          >
                            <option value="">Select Project Manager</option>
                            {manningList.map((m) => (
                              <option
                                key={m.manning_info_id}
                                value={m.manning_info_id}
                              >
                                {m.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* Discipline Total Hours */}
                        <div className="field-block discipline-hours-field">
                          <label>Total Discipline Hrs.</label>
                          <input
                            type="number"
                            min={0}
                            value={disciplineTotalHours}
                            onChange={(e) => {
                              const val = e.target.value;
                              setDisciplineTotalHours(val === '' ? '' : Number(val));
                            }}
                          />
                        </div>

                        {/* Generate Forecast Sheet */}
                        <button
                          className="project-generate-forecast-button"
                          disabled={
                            !tempDiscipline ||
                            !startDate ||
                            !endDate ||
                            !projectManager ||
                            disciplineTotalHours === '' ||
                            disciplineTotalHours <= 0 ||
                            isGeneratingForecast
                          }
                          onClick={handleGenerateForecastSheet}
                        >
                          {isGeneratingForecast
                            ? 'Generating...'
                            : 'Generate Forecast Sheet'}
                        </button>
                      </div>

                      {/* BOTTOM: JUST-ADDED / CURRENT FORECAST SECTION */}
                      <div className="bottom-forecast-section">
                        <h3 className="forecast-section-title">
                          {selectedProject?.project_name}
                          <span className="project-code-label">({selectedProject?.project_code})</span>
                        </h3>


                        {projectForecasts.length === 0 && (
                          <p>No forecasts found for this project.</p>
                        )}

                        {projectForecasts.map((forecast, idx) => {
                          const disciplineCode = forecast.discipline;
                          const { weeks, byManning } =
                            buildManningDisplayData(disciplineCode);

                          return (
                          <div key={disciplineCode} className="forecast-card">

                            {/* Discipline Header Row */}
                          <div className="forecast-card-header">
                            <div className="forecast-card-title">
                              <div className="forecast-title-main">
                                <strong>{getDisciplineName(disciplineCode)}</strong>

                                {typeof forecast.total_discipline_hours === "number" && (
                                  <span className="discipline-total-hours">
                                    ({forecast.total_discipline_hours} hrs)
                                  </span>
                                )}
                              </div>

                              {/* ADD TASK BUTTON — only if NO tasks exist */}
                              {(!disciplineTasks[disciplineCode] ||
                                disciplineTasks[disciplineCode].length === 0) && (
                                <button
                                  className="icon-btn add-task"
                                  onClick={() => openTaskModal(disciplineCode)}
                                >
                                  ＋ Task
                                </button>


                              )}


                              {/* TASK ICON + TOOLTIP */}
                              {/* TASK ICON + POPOVER */}
                              {disciplineTasks[disciplineCode]?.length > 0 && (
                                <div className="task-popover-container">
                                  <button
                                    className="icon-btn task-icon"
                                    onClick={() => {
                                      setOpenTaskDiscipline(
                                        openTaskDiscipline === disciplineCode ? null : disciplineCode
                                      );
                                      fetchDisciplineTasks(disciplineCode);
                                    }}
                                  >
                                    📋
                                  </button>

                                  {openTaskDiscipline === disciplineCode && (
                                    <div className="task-popover">
                                      <div className="task-popover-header">
                                        Tasks – {selectedProject?.project_code} / {getDisciplineName(disciplineCode)}
                                      </div>

                                      <div className="task-popover-body">
                                        {disciplineTasks[disciplineCode].map((t) => (
                                          <div
                                            key={t.project_discipline_task_id}
                                            className="task-popover-row"
                                          >
                                            <span className="task-name">{t.task_description}</span>
                                            <span className="task-hours">{t.total_forecasted_hours} h</span>
                                          </div>
                                        ))}
                                      </div>

                                      <div className="task-popover-footer">
                                        <button
                                          className="btn-link"
                                          onClick={() => {
                                            setOpenTaskDiscipline(null);
                                            openTaskModal(disciplineCode);
                                          }}
                                        >
                                          ✎
                                        </button>

                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                            </div>

                            {/* Editable vs normal display */}
                            {editingDiscipline === disciplineCode ? (
                              <>
                                <div className="forecast-card-dates">
                                  <input
                                    type="date"
                                    value={editDisciplineState.start}
                                    onChange={(e) =>
                                      setEditDisciplineState({ ...editDisciplineState, start: e.target.value })
                                    }
                                  />
                                  <span> → </span>
                                  <input
                                    type="date"
                                    value={editDisciplineState.end}
                                    onChange={(e) =>
                                      setEditDisciplineState({ ...editDisciplineState, end: e.target.value })
                                    }
                                  />
                                </div>

                                <div className="forecast-card-pm">
                                  <select
                                    value={editDisciplineState.pm}
                                    onChange={(e) =>
                                      setEditDisciplineState({ ...editDisciplineState, pm: e.target.value })
                                    }
                                  >
                                    {manningList.map((m) => (
                                      <option key={m.manning_info_id} value={m.manning_info_id}>
                                        {m.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className="forecast-card-hours">
                                  <input
                                    type="number"
                                    min={0}
                                    value={editDisciplineState.totalHours}
                                    onChange={(e) =>
                                      setEditDisciplineState({
                                        ...editDisciplineState,
                                        totalHours:
                                          e.target.value === "" ? "" : Number(e.target.value)
                                      })
                                    }
                                    placeholder="Total Discipline Hours"
                                  />
                                </div>

                                <div className="forecast-card-icons">
                                  <button
                                    className="icon-btn save"
                                    onClick={() => handleSaveDisciplineInfo(disciplineCode)}
                                  >
                                    ✓
                                  </button>
                                  <button
                                    className="icon-btn cancel"
                                    onClick={() => setEditingDiscipline(null)}
                                  >
                                    ✕
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="forecast-card-dates">
                                  {formatDate(forecast.project_start_date)} → {formatDate(forecast.project_end_date)}
                                </div>

                                <div className="forecast-card-pm">
                                  PM: {getProjectManagerName(forecast.project_manager_manning_id)}
                                </div>

                                <div className="forecast-card-icons">
                                  <button
                                    className="icon-btn"
                                    onClick={() => {
                                      setEditingDiscipline(disciplineCode);
                                      setEditDisciplineState({
                                        start: forecast.project_start_date.slice(0, 10),
                                        end: forecast.project_end_date.slice(0, 10),
                                        pm: String(forecast.project_manager_manning_id),
                                        totalHours: forecast.total_discipline_hours ?? ""
                                      });
                                    }}
                                  >
                                    ✎
                                  </button>

                                  <button
                                    className="icon-btn delete"
                                    onClick={() => handleDeleteDiscipline(disciplineCode)}
                                  >
                                    🗑
                                  </button>
                                </div>
                              </>
                            )}
                          </div>


                            {/* Manning Table */}
                            {byManning.length > 0 && (
                            <div className="manning-scroll-wrapper">
                            <div className="forecast-manning-grid">

                              {/* Column Headers */}
                              <div className="forecast-grid-row forecast-grid-header">
                                <div className="forecast-grid-cell manning-col">Manning</div>
                                {weeks.map((w) => (
                                  <div key={w} className="forecast-grid-cell week-col">
                                    {formatWeekLabel(w)}
                                  </div>
                                ))}
                                <div className="forecast-grid-cell action-col">Edit</div>
                              </div>

                              {/* Rows */}
                            {byManning.map((m) => {
                              const isEditing =
                                editingRow &&
                                editingRow.discipline === disciplineCode &&
                                editingRow.manningId === m.manning_info_id;

                              return (
                                <div key={m.manning_info_id} className="forecast-grid-row">
                                  <div className="forecast-grid-cell manning-col">
                                    <div className="manning-task-row">
                                    <span
                                      className="manning-name clickable"
                                      onClick={() => handleManningClick(m.manning_info_id)}
                                    >
                                      {getManningNameById(m.manning_info_id)}
                                    </span>
                                    <span> - </span>


                                      <span className="task-name">
                                        {getTaskDescription(
                                          selectedProject!.project_code,
                                          projectForecastManningHours[disciplineCode]
                                            ?.find(r => String(r.manning_info_id) === String(m.manning_info_id))
                                            ?.project_discipline_task_id
                                        )}
                                      </span>
                                    </div>
                                  </div>

                                  {weeks.map((w) => {
                                    // 🔹 compute inside the map — correct scope
                                    const totalHoursMap = totalHoursByManning[m.manning_info_id] ?? {};
                                    const currentTotal = totalHoursMap[w] ?? 0;
                                    const isAtLimit = currentTotal >= 40;

                                    return (
                                      <div key={w} className="forecast-grid-cell">
                                        {isEditing ? (
                                          <input
                                            className={`forecast-edit-input ${isAtLimit ? "limit-reached" : ""}`}
                                            type="number"
                                            title={isAtLimit ? "Already forecasted 40hrs!" : ""}
                                            value={
                                              projectForecastManningHours[disciplineCode]
                                                ?.find(
                                                  r =>
                                                    String(r.manning_info_id) === String(m.manning_info_id) &&
                                                    r.weekdate === w
                                                )?.hours ?? 0
                                            }
                                            onChange={(e) => {
                                              const newHours = Number(e.target.value);

                                              setProjectForecastManningHours((prev) => {
                                                const updated = { ...prev };
                                                updated[disciplineCode] = updated[disciplineCode].map((row) =>
                                                  String(row.manning_info_id) === String(m.manning_info_id) &&
                                                  row.weekdate === w
                                                    ? { ...row, hours: newHours }
                                                    : row
                                                );
                                                return updated;
                                              });
                                            }}
                                          />
                                        ) : (
                                          m.hoursByWeek[w] ?? ""
                                        )}
                                      </div>
                                    );
                                  })}


                                  {/* ACTION BUTTONS */}
                                  <div className="forecast-grid-cell action-col">
                                                {isEditing ? (
                                      <div className="forecast-card-icons">
                                        <button
                                          className="icon-btn save"
                                          onClick={() =>
                                            handleSaveEditedBottomRow(disciplineCode, m.manning_info_id)
                                          }
                                        >
                                          ✓
                                        </button>
                                        <button
                                          className="icon-btn cancel"
                                          onClick={() => setEditingRow(null)}
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="forecast-card-icons">
                                        <button
                                          className="icon-btn edit"
                                          onClick={async () => {
                                            const manningId = m.manning_info_id;

                                            const rows = projectForecastManningHours[disciplineCode] || [];
                                            const snap: Record<string, number> = {};
                                            rows
                                              .filter(r => String(r.manning_info_id) === String(manningId))
                                              .forEach(r => {
                                                snap[r.weekdate.slice(0,10)] = Number(r.hours ?? 0);
                                              });

                                            setOriginalHoursByRow(prev => ({
                                              ...prev,
                                              [`${disciplineCode}_${manningId}`]: snap
                                            }));

                                            setEditingRow({ discipline: disciplineCode, manningId });
                                          }}
                                        >
                                          ✎
                                        </button>


                                        {/* 🗑 DELETE BUTTON */}
                                        <button
                                          className="icon-btn delete"
                                          onClick={() => {
                                            //if (window.confirm("Delete this manning row and all its hours?")) {
                                              handleDeleteManningHoursRow(disciplineCode, m.manning_info_id);
                                            //}
                                          }}
                                        >
                                          🗑
                                        </button>
                                      </div>
                                    )}

                                  </div>
                                </div>
                              );
                            })}

                            </div>
                          </div>
                            )}

                            {/* Add Manning Entry Row — Only after first manning added */}
                            {showAddManningForDiscipline[disciplineCode] && (() => {
                              const weekDates = mondayDatesByDiscipline[disciplineCode] ?? [];

                              if (!weekDates.length) return null;
 
                              return (
                                <div className="forecast-sheet new-manning-entry">
                                  <div className="manning-add-scroll-wrapper">

                                    <div
                                      className="manning-add-grid"
                                      style={{
                                        gridTemplateColumns: `200px 220px repeat(${weekDates.length}, 80px) 60px`
                                      }}
                                    >
                                      {/* HEADER ROW */}
                                      <div className="header-cell manning-header"></div>
                                      <div className="header-cell task-header"></div>
                                      {weekDates.map((d) => (
                                        <div key={d} className="header-cell week-header">
                                          {formatWeekLabel(d)}
                                        </div>
                                      ))}

                                      <div className="header-cell"></div>

                                      {/* ADD MANNING SELECT */}
                                      <select
                                          className="sheet-manning-dropdown"
                                          value={hoursRows[0]?.manning_info_id ?? ""}
                                          onChange={async (e) => {
                                            const manningId = e.target.value;
                                            updateRowManning(0, manningId);

                                            if (!manningId) return;
                                            if (!showAvailabilityCalendar) return;

                                            const forecast = projectForecasts.find(
                                              f => f.discipline === disciplineCode
                                            );
                                            if (!forecast) return;

                                            setAbsenceContext({
                                              discipline: disciplineCode,
                                              manningId,
                                              start: forecast.project_start_date.slice(0, 10),
                                              end: forecast.project_end_date.slice(0, 10),
                                            });

                                            setShowAbsenceModal(true);
                                          }}
                                        >
                                          <option value="">
                                            Add Manning to {getDisciplineName(disciplineCode)}
                                          </option>

                                          {(() => {
                                            const addedManningIds = getAddedManningIdsForDiscipline(disciplineCode);

                                            return manningList
                                              .filter(m => !addedManningIds.has(String(m.manning_info_id)))
                                              .map(m => (
                                                <option key={m.manning_info_id} value={m.manning_info_id}>
                                                  {m.name}
                                                </option>
                                              ));
                                          })()}
                                      </select>


                                      {/* 🔹 TASK SELECT */}
                                      {(() => {
                                        // lazy-load tasks only when this row renders
                                        const tasks = disciplineTasks[disciplineCode] ?? [];
                                        const selectedTask = tasks.find(
                                          (t) =>
                                            String(t.project_discipline_task_id) ===
                                            String(selectedTaskByDiscipline[disciplineCode])
                                        );

                                        return (
                                          <select
                                            className="sheet-task-dropdown"
                                            value={selectedTaskByDiscipline[disciplineCode] ?? ""}
                                            title={
                                              selectedTask
                                                ? `${selectedTask.task_description} — Total: ${selectedTask.total_forecasted_hours}h, Remaining: ${selectedTask.remaining_hours}h`
                                                : "Select a task"
                                            }
                                            onChange={(e) => {
                                              const value = e.target.value;

                                              if (value === "__ADD_TASK__") {
                                                openTaskModal(disciplineCode);
                                                return;
                                              }

                                              setSelectedTaskByDiscipline((prev) => ({
                                                ...prev,
                                                [disciplineCode]: value,
                                              }));
                                            }}
                                          >
                                            <option value="">Select Task</option>

                                            {tasks.map((t) => (
                                              <option
                                                key={t.project_discipline_task_id}
                                                value={t.project_discipline_task_id}
                                                disabled={t.remaining_hours <= 0}
                                                 style={
                                                    t.remaining_hours <= 0
                                                      ? { color: "#b00020", fontWeight: 600 }
                                                      : undefined
                                                  }
                                              >
                                                 {t.task_description} —  (T: {t.total_forecasted_hours}h, R: {t.remaining_hours}h)
                                                 {t.remaining_hours <= 0 ? " ⚠ Fully Used" : ""}
                                              </option>
                                            ))}

                                            <option value="__ADD_TASK__">➕ Add New Task</option>
                                          </select>
                                        );
                                      })()}



                                        {weekDates.map((dateIso, colIndex) => (
                                          <div key={dateIso} className="sheet-cell">
                                            <button
                                              type="button"
                                              className="cell-add-btn"
                                              onClick={() =>
                                                handleAddSingleHour(disciplineCode, 0, colIndex)
                                              }
                                            >
                                              ✓
                                            </button>

                                            <input
                                              type="number"
                                              className="sheet-input"
                                              value={hoursRows[0]?.hours?.[colIndex] ?? 0}
                                              onChange={(e) =>
                                                updateHourInput(0, colIndex, Number(e.target.value))
                                              }
                                            />
                                          </div>
                                        ))}


                                      <button
                                        className="row-save-btn"
                                        onClick={() => handleAddManningForecast(disciplineCode, 0)}
                                      >
                                        ✓
                                      </button>
                                    </div>

                                    <div className="availability-toggle">
                                        <label className="availability-checkbox">
                                          <input
                                            type="checkbox"
                                            checked={showAvailabilityCalendar}
                                            onChange={(e) => setShowAvailabilityCalendar(e.target.checked)}
                                          />
                                          Show Manning Availability Calendar
                                        </label>
                                      </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        {showTaskModal && taskModalDiscipline && (
        <div className="modal-overlay">
          <div className="modal modal-task">
            <div className="modal-header">
              <h3 className="modal-title-inline">
                Add Task
                <span className="modal-subtitle-inline">
                  {selectedProject?.project_code} – {getDisciplineName(taskModalDiscipline)}
                </span>
              </h3>

            </div>

            <div className="modal-body">
              {modalTasks.length > 0 && (
                <div className="modal-task-list">
                  <div className="modal-task-list-title">Existing Tasks</div>

                  {modalTasks.map((task) => {
                    const isEditing = editingTaskId === task.project_discipline_task_id;

                    return (
                      <div
                        key={task.project_discipline_task_id}
                        className="modal-task-row"
                      >
                        {isEditing ? (
                          <>
                            <input
                              className="task-edit-input"
                              value={editTaskDescription}
                              onChange={(e) => setEditTaskDescription(e.target.value)}
                            />

                            <input
                              type="number"
                              min={0}
                              className="task-edit-hours"
                              value={editTaskHours}
                              onChange={(e) => setEditTaskHours(Number(e.target.value))}
                            />

                            <button
                              className="icon-btn save"
                              disabled={isSavingTask}
                              onClick={() =>
                                handleSaveTaskEdit(task.project_discipline_task_id)
                              }
                            >
                              ✓
                            </button>

                            <button
                              className="icon-btn cancel"
                              onClick={() => setEditingTaskId(null)}
                            >
                              ✕
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="task-name">
                              {task.task_description}
                            </span>

                            <span className="task-hours">
                              {task.total_forecasted_hours} hrs
                            </span>

                            <button
                              className="icon-btn edit"
                              title="Edit task"
                              onClick={() => {
                                setEditingTaskId(task.project_discipline_task_id);
                                setEditTaskDescription(task.task_description);
                                setEditTaskHours(task.total_forecasted_hours);
                              }}
                            >
                              ✎
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}

                </div>
              )}

              <div className="modal-field">
                <label>Task Description</label>
                <input
                  type="text"
                  placeholder="e.g. Panel wiring, FAT support…"
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                />
              </div>

              <div className="modal-field">
                <label>Total Forecasted Hours</label>
                <input
                  type="number"
                  min={0}
                  value={taskHours}
                  onChange={(e) => setTaskHours(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn primary" onClick={handleAddTask}>
                Add Task
              </button>
              <button
                className="btn secondary"
                onClick={() => {
                  setShowTaskModal(false);
                  setTaskDescription("");
                  setTaskHours(0);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        )}

        {showProjectGroupModal && (
          <div className="modal-overlay">
            <div className="modal modal-task">

              <div className="modal-header">
                <h3>Add New Project Group</h3>
              </div>

              <div className="modal-body">
                <div className="modal-field">
                  <label>Project Group Description</label>
                  <input
                    type="text"
                    value={newProjectGroupDescription}
                    onChange={(e) => setNewProjectGroupDescription(e.target.value)}
                  />
                </div>

                <div className="modal-field">
                  <label>Project Code</label>
                  <input
                    type="text"
                    value={newProjectGroupProjectCode}
                    onChange={(e) => setNewProjectGroupProjectCode(e.target.value)}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="btn primary"
                  disabled={isSavingProjectGroup}
                  onClick={saveProjectGroup}
                >
                  {isSavingProjectGroup ? "Saving..." : "Save"}
                </button>

                <button
                  className="btn secondary"
                  onClick={() => setShowProjectGroupModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showEditProjectGroupModal && editingProjectGroup && (
          <div className="modal-overlay">
            <div className="modal modal-task">

              <div className="modal-header">
                <h3>Edit Project Group</h3>
              </div>

              <div className="modal-body">

                {/* Description */}
                <div className="modal-field">
                  <label>Project Group Description</label>
                  <input
                    type="text"
                    value={editProjectGroupDescription}
                    onChange={(e) => setEditProjectGroupDescription(e.target.value)}
                  />
                </div>

                {/* Existing Projects */}
                <div className="modal-task-list">
                  <div className="modal-task-list-title">Projects in Group</div>

                  {editProjectGroupProjects.map((p) => (
                    <div key={p.project_group_project_id} className="modal-task-row">
                      <span>{p.project_code}</span>
                      <button
                        className="icon-btn delete"
                        onClick={() =>
                          deleteProjectFromGroup(p.project_group_project_id)
                        }
                      >
                        🗑
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add Project */}
                <div className="modal-field">
                  <label>Project Code</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="text"
                      value={newProjectCodeToAdd}
                      onChange={(e) => setNewProjectCodeToAdd(e.target.value)}
                    />
                    <button className="icon-btn add" onClick={addProjectToGroup}>
                      ➕
                    </button>
                  </div>
                </div>

              </div>

              <div className="modal-actions">
                <button
                  className="btn primary"
                  onClick={async () => {
                    await updateProjectGroupDescription();
                    await handleCloseEditProjectGroupModal();
                  }}
                >
                  Save
                </button>

                <button
                  className="btn secondary"
                  onClick={handleCloseEditProjectGroupModal}
                >
                  Close
                </button>
              </div>


            </div>
          </div>
        )}

        {showHelpVideo && videoUrl && (
          <div className="modal-overlay">
            <div className="modal video-modal">
              <h3>Project Help</h3>

              <video
                controls
                autoPlay
                style={{ width: "100%", borderRadius: "6px" }}
              >
                <source src={videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              <div className="modal-actions">
                <button
                  className="button_w100 secondary"
                  onClick={() => setShowHelpVideo(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}


        <ManningModal
          isOpen={isManningModalOpen}
          onClose={() => {
            setIsManningModalOpen(false);
            setEditingManning(null);
          }}
          onSave={() => {
            // optional: refresh manning-related UI
          }}
          onRefresh={async () => {
            // refresh manning list after edit
            const url =
              "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
              "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
              "&action=get_all_manning_pros";

            const data = await fetchWithAuth(url);
            setManningList(data);
            localStorage.setItem("all_manning", JSON.stringify(data));
          }}
          regionCode={regionCode ?? null}
          uniqueLeaders={uniqueLeaders}
          editingManning={editingManning}
          isEditing={true}
        />

        {showAbsenceModal && absenceContext && (
        <AbsenceModal
          ref={absenceModalRef}
          isOpen={showAbsenceModal}
          start={absenceContext.start}
          end={absenceContext.end}
          manningInfoId={absenceContext.manningId}
          onClose={() => setShowAbsenceModal(false)}
          onSave={(manningInfoId, payload) => saveAbsences(manningInfoId, payload)}

        />
        )}


      </main>
    </div>
  );
};

export default Project;
